import React, { useState, useEffect, useRef } from "react";

import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { useNavigate, useLocation } from "react-router-dom";

// Import shared components
import FeaturedGames from "../components/FeaturedGames";
import FollowButton from "../components/FollowButton";
import GamesToWatch from "../components/GamesToWatch";
import LeftMenu from "../components/LeftMenu";
import MobileMenu from "../components/MobileMenu";
import PremiumOverlay from "../components/PremiumOverlay";
import { LEAGUES, SPORT_ICONS } from "../data/leaguesConfig";
import {
  getTeamByName,
  searchTeams,
  TEAMS_DATABASE,
} from "../data/teamsDatabase";
import apiService from "../services/api";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import { isPremiumUser } from "../utils/premiumUtils";
import { sportsDbFetch } from "../utils/sportsDbCache";
import {
  getFavoriteTeams,
  toggleFavoriteTeam,
  getPersonalizedFeaturedGames,
  getPersonalizedLiveNowOrder,
  getBecauseYouFollowRails,
  getFollowedPlayers,
} from "../utils/sportsPreferences";
import webPlayerStorage from "../utils/webPlayerStorage";

// Helper to safely extract numeric values from score/goal/point objects
const getNumericValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseInt(value, 10);

    return isNaN(parsed) ? null : parsed;
  }
  if (typeof value === "object") {
    // Try different possible nested properties
    return (
      value.total ||
      value.for ||
      value.home ||
      value.away ||
      value.value ||
      null
    );
  }

  return null;
};

// Normalize event scores to prevent React rendering errors
const normalizeEventScores = (event) => {
  if (!event) return event;

  return {
    ...event,
    // Normalize scores if they exist
    ...(event.scores && {
      scores: {
        home: getNumericValue(event.scores.home),
        away: getNumericValue(event.scores.away),
      },
    }),
    ...(event.goals && {
      goals: {
        home: getNumericValue(event.goals.home),
        away: getNumericValue(event.goals.away),
      },
    }),
  };
};

// Hall of Fame players — verified TheSportsDB IDs with strCutout
const HOF_PLAYERS = [
  { id: "34165220", name: "Patrick Mahomes", sport: "American Football" },
  { id: "34160611", name: "Stephen Curry", sport: "Basketball" },
  { id: "34146371", name: "Neymar", sport: "Soccer" },
  { id: "34162098", name: "Kylian Mbappe", sport: "Soccer" },
  { id: "34145506", name: "Mohamed Salah", sport: "Soccer" },
  { id: "34169116", name: "Erling Haaland", sport: "Soccer" },
  { id: "34161176", name: "Rafael Nadal", sport: "Tennis" },
  { id: "34161173", name: "Roger Federer", sport: "Tennis" },
  { id: "34176741", name: "Connor McDavid", sport: "Ice Hockey" },
  { id: "34185573", name: "Shohei Ohtani", sport: "Baseball" },
];

// Top teams by total honours (from TheSportsDB Hall of Fame)
const HOF_TEAMS = [
  { name: "Celtic", honours: 119, sport: "Soccer" },
  { name: "Rangers", honours: 117, sport: "Soccer" },
  { name: "Al Ahly", honours: 112, sport: "Soccer" },
  { name: "Real Madrid", honours: 100, sport: "Soccer" },
  { name: "Barcelona", honours: 92, sport: "Soccer" },
  { name: "Bayern Munich", honours: 83, sport: "Soccer" },
  { name: "Manchester United", honours: 68, sport: "Soccer" },
  { name: "Juventus", honours: 60, sport: "Soccer" },
  { name: "Liverpool", honours: 50, sport: "Soccer" },
  { name: "AC Milan", honours: 48, sport: "Soccer" },
  { name: "Los Angeles Lakers", honours: 40, sport: "Basketball" },
  { name: "New York Yankees", honours: 37, sport: "Baseball" },
];

// Sport-colored gradient rings for HOF avatars
const SPORT_RING_COLORS = {
  "American Football": "from-blue-500 to-blue-700",
  Basketball: "from-orange-500 to-orange-700",
  Soccer: "from-green-500 to-green-700",
  Tennis: "from-yellow-500 to-yellow-700",
  "Ice Hockey": "from-cyan-500 to-cyan-700",
  Baseball: "from-red-500 to-red-700",
};

const WebPlayerSportsTest = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get playlist and profile from navigation state or storage
  const playlist = location.state?.playlist || webPlayerStorage.getPlaylist();
  const profile = location.state?.profile || webPlayerStorage.getProfile();

  // Check if video has been played this session and if we have auth
  const [sportsData, setSportsData] = useState({
    carousel: [],
    upcoming: [],
    past: [],
  });
  const [loading, setLoading] = useState(true);
  const [leagues, setLeagues] = useState([]);
  // Only show premium overlay if user is authenticated but not premium
  // Logged-out users should not see the premium overlay on the sports page
  const isAuthenticated = !!webPlayerStorage.getToken();
  const [showPremiumOverlay, setShowPremiumOverlay] = useState(
    isAuthenticated && !isPremiumUser()
  );

  // Spoiler-free mode
  const [spoilerFree, setSpoilerFree] = useState(
    () => localStorage.getItem("lunova_spoiler_free") === "true"
  );
  const toggleSpoilerFree = () => {
    setSpoilerFree((prev) => {
      const next = !prev;

      localStorage.setItem("lunova_spoiler_free", String(next));

      return next;
    });
  };

  // Superfan mode - single-game command center for a favorite team
  const [superfanTeam, setSuperfanTeam] = useState(
    () => localStorage.getItem("lunova_superfan_team") || null
  );
  const [superfanEvents, setSuperfanEvents] = useState([]); // real-time event feed
  const [superfanCallout, setSuperfanCallout] = useState(null); // player tracking callout
  const superfanCalloutTimer = useRef(null);

  const toggleSuperfan = (teamName) => {
    setSuperfanTeam((prev) => {
      const next = prev === teamName ? null : teamName;

      if (next) {
        localStorage.setItem("lunova_superfan_team", next);
      } else {
        localStorage.removeItem("lunova_superfan_team");
      }

      return next;
    });
  };

  // AI Game Recap - template-based recap for finished followed-team games
  const [gameRecaps, setGameRecaps] = useState([]);

  // Heartbeat mode - live-updating sparkline ticker for Live Now strip
  const [heartbeatMode, setHeartbeatMode] = useState(false);
  const [heartbeatData, setHeartbeatData] = useState({}); // { gameId: [score snapshots] }
  const [heartbeatPulse, setHeartbeatPulse] = useState({}); // { gameId: "home"|"away"|null }

  // Pick Your Teams overlay state
  const [showTeamPicker, setShowTeamPicker] = useState(false);
  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const [teamSearchResults, setTeamSearchResults] = useState([]);
  const [followedTeams, setFollowedTeams] = useState(getFavoriteTeams());
  const [followedPlayersList] = useState(getFollowedPlayers());
  const teamSearchInputRef = useRef(null);

  // New section data
  const [leagueBadges, setLeagueBadges] = useState({});
  const [teamColors, setTeamColors] = useState({});
  const [hofPlayers, setHofPlayers] = useState([]);
  const [hofTeamData, setHofTeamData] = useState([]);
  const [highlights, setHighlights] = useState([]);

  const emblaOpts = {
    loop: false,
    align: "start",
    containScroll: "trimSnaps",
    dragFree: true,
    axis: "x",
  };
  const emblaPlugins = [WheelGesturesPlugin({ forceWheelAxis: "x" })];

  const [sportPillsRef] = useEmblaCarousel(emblaOpts, emblaPlugins);
  const [liveNowEmblaRef] = useEmblaCarousel(emblaOpts, emblaPlugins);
  const [myTeamsEmblaRef] = useEmblaCarousel(emblaOpts, emblaPlugins);
  const [myPlayersEmblaRef] = useEmblaCarousel(emblaOpts, emblaPlugins);
  const [leaguesEmblaRef] = useEmblaCarousel(emblaOpts, emblaPlugins);
  const [discoverTeamsEmblaRef] = useEmblaCarousel(emblaOpts, emblaPlugins);
  const [hofEmblaRef] = useEmblaCarousel(emblaOpts, emblaPlugins);
  const [highlightsEmblaRef] = useEmblaCarousel(emblaOpts, emblaPlugins);

  // Fetch sports data from API
  useEffect(() => {
    const fetchSportsData = async () => {
      try {
        // Get current date and time
        const now = new Date();
        const timestamp = now.toISOString().slice(0, 19); // Format: YYYY-MM-DDTHH:mm:ss
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // First, fetch the home data which includes carousel - most important for initial render
        apiService
          .webPlayerRequest(
            `/v2/sports/home?timestamp=${timestamp}&timezone=${encodeURIComponent(timezone)}`
          )
          .then((homeResponse) => {
            const homeData = homeResponse?.data ||
              homeResponse || { carousel: [], live: [], upcoming: [] };

            // The home endpoint returns three arrays:
            //   carousel — small featured subset (~20)
            //   live     — every game currently live (~100+)
            //   upcoming — every game scheduled for today (~300+)
            // Merge featured + live for the main view so followed teams outside
            // the curated carousel still surface.
            const live = (homeData.live || []).filter((e) => e != null);
            const featured = (homeData.carousel || []).filter((e) => e != null);
            const seen = new Set();
            const combinedLive = [...featured, ...live].filter((g) => {
              const id = g?.id || `${g?.home?.name}-${g?.away?.name}`;

              if (seen.has(id)) return false;
              seen.add(id);

              return true;
            });

            setSportsData((prev) => ({
              ...prev,
              carousel: combinedLive.map(normalizeEventScores),
              upcoming: (homeData.upcoming || [])
                .filter((e) => e != null)
                .map(normalizeEventScores),
            }));
            setLoading(false);
          })
          .catch(() => {
            setSportsData((prev) => ({
              ...prev,
              carousel: [],
            }));
            setLoading(false);
          });

        // Then fetch upcoming and past events separately (non-blocking) - SLICE to 10 only
        apiService
          .webPlayerRequest(`/v2/sports/events/upcoming?timestamp=${timestamp}`)
          .then((upcomingResponse) => {
            const upcomingData =
              upcomingResponse?.data || upcomingResponse || [];

            // Handle both array and object structures
            let eventsArray = [];

            if (Array.isArray(upcomingData)) {
              eventsArray = upcomingData.filter((event) => event != null);
            } else if (
              typeof upcomingData === "object" &&
              upcomingData !== null
            ) {
              eventsArray = Object.values(upcomingData)
                .flat()
                .filter((event) => event != null);
            }

            // Merge with whatever home returned, dedup by id/teams.
            if (eventsArray.length > 0) {
              setSportsData((prev) => {
                const seen = new Set();
                const merged = [...(prev.upcoming || []), ...eventsArray]
                  .map(normalizeEventScores)
                  .filter((g) => {
                    const id =
                      g?.id || `${g?.home?.name}-${g?.away?.name}-${g?.date}`;

                    if (seen.has(id)) return false;
                    seen.add(id);

                    return true;
                  });

                return { ...prev, upcoming: merged };
              });
            }
          })
          .catch(() => {
            // Keep existing upcoming data from home response
          });

        apiService
          .webPlayerRequest(`/v2/sports/events/past`)
          .then((pastResponse) => {
            const pastData = pastResponse?.data || pastResponse || {};
            const pastEvents = Object.values(pastData)
              .flat()
              .filter((event) => event != null);

            // SLICE TO ONLY 10 EVENTS
            const limitedEvents = pastEvents.slice(0, 10);

            if (limitedEvents.length > 0) {
              setSportsData((prev) => ({
                ...prev,
                past: limitedEvents.map(normalizeEventScores),
              }));
            }
          })
          .catch(() => {
            // Keep existing past data from home response
          });
      } catch (error) {
        console.error("Error fetching sports data:", error);
        setLoading(false);
      }
    };

    // Initial fetch - get all data on mount
    fetchSportsData();

    // Set up polling for ONLY carousel updates (live scores) every 15 seconds
    // Don't re-fetch upcoming/past events to prevent flickering
    const pollingInterval = setInterval(() => {
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Only fetch carousel for live score updates
      apiService
        .webPlayerRequest(
          `/v2/sports/home?timestamp=${timestamp}&timezone=${encodeURIComponent(timezone)}`
        )
        .then((homeResponse) => {
          const homeData = homeResponse?.data ||
            homeResponse || { carousel: [], live: [] };

          // Re-merge featured + live for live score updates.
          const live = (homeData.live || []).filter((e) => e != null);
          const featured = (homeData.carousel || []).filter((e) => e != null);
          const seen = new Set();
          const combinedLive = [...featured, ...live].filter((g) => {
            const id = g?.id || `${g?.home?.name}-${g?.away?.name}`;

            if (seen.has(id)) return false;
            seen.add(id);

            return true;
          });

          setSportsData((prev) => ({
            ...prev,
            carousel: combinedLive.map(normalizeEventScores),
          }));
        })
        .catch(() => {
          // Keep existing data on error
        });
    }, 15000);

    // Cleanup interval on unmount
    return () => clearInterval(pollingInterval);
  }, []);

  // Sport categories for navigation pills
  useEffect(() => {
    const sportCategories = [
      { id: "football", name: "Football", icon: "fa-futbol" },
      { id: "basketball", name: "Basketball", icon: "fa-basketball-ball" },
      {
        id: "american-football",
        name: "American Football",
        icon: "fa-football-ball",
      },
      { id: "baseball", name: "Baseball", icon: "fa-baseball-ball" },
      { id: "hockey", name: "Hockey", icon: "fa-hockey-puck" },
      { id: "motorsports", name: "Motorsports", icon: "fa-flag-checkered" },
      { id: "fighting", name: "Fighting", icon: "fa-fist-raised" },
      { id: "rugby", name: "Rugby", icon: "fa-football-ball" },
      { id: "volleyball", name: "Volleyball", icon: "fa-volleyball-ball" },
      { id: "afl", name: "AFL", icon: "fa-football-ball" },
      { id: "cricket", name: "Cricket", icon: "fa-cricket" },
      { id: "racket-sports", name: "Racket Sports", icon: "fa-table-tennis" },
      { id: "golf", name: "Golf", icon: "fa-golf-ball" },
      { id: "other", name: "Other", icon: "fa-trophy" },
    ];

    setLeagues(sportCategories);
  }, []);

  // Fetch league badges from TheSportsDB
  useEffect(() => {
    const fetchLeagueBadges = async () => {
      const badges = {};
      const leaguesWithIds = LEAGUES.filter((l) => l.sportsDbId);

      await Promise.allSettled(
        leaguesWithIds.map(async (league) => {
          try {
            const data = await sportsDbFetch(
              `lookupleague.php?id=${league.sportsDbId}`
            );

            if (data?.leagues?.[0]) {
              badges[league.id] = {
                badge: data.leagues[0].strBadge,
                name: data.leagues[0].strLeague,
                sport: data.leagues[0].strSport,
                country: data.leagues[0].strCountry,
              };
            }
          } catch (e) {
            /* ignore */
          }
        })
      );
      setLeagueBadges(badges);
    };

    fetchLeagueBadges();
  }, []);

  // Fetch team colors from TheSportsDB (batch by league)
  useEffect(() => {
    const SPORTSDB_LEAGUES = [
      4328, 4335, 4331, 4332, 4334, 4391, 4387, 4380, 4424, 4346,
    ];
    const fetchTeamColors = async () => {
      const colors = {};

      await Promise.allSettled(
        SPORTSDB_LEAGUES.map(async (leagueId) => {
          try {
            const data = await sportsDbFetch(
              `lookup_all_teams.php?id=${leagueId}`
            );

            (data?.teams || []).forEach((t) => {
              if (t.strColour1)
                colors[t.strTeam] = {
                  primary: t.strColour1,
                  secondary: t.strColour2 || t.strColour1,
                  badge: t.strBadge,
                };
            });
          } catch (e) {
            /* ignore */
          }
        })
      );
      setTeamColors(colors);
    };

    fetchTeamColors();
  }, []);

  // Fetch Hall of Fame player data from TheSportsDB
  useEffect(() => {
    const fetchHof = async () => {
      const players = [];

      await Promise.allSettled(
        HOF_PLAYERS.map(async (p) => {
          try {
            const data = await sportsDbFetch(`lookupplayer.php?id=${p.id}`);

            if (data?.players?.[0]) {
              const pl = data.players[0];

              players.push({
                id: pl.idPlayer,
                name: pl.strPlayer,
                cutout: pl.strCutout,
                nationality: pl.strNationality,
                sport: pl.strSport,
                team: pl.strTeam,
                position: pl.strPosition,
              });
            }
          } catch (e) {
            /* ignore */
          }
        })
      );
      setHofPlayers(players);
    };

    fetchHof();
  }, []);

  // Fetch Hall of Fame team data from TheSportsDB (searchteams.php)
  useEffect(() => {
    const fetchHofTeams = async () => {
      const teams = [];

      await Promise.allSettled(
        HOF_TEAMS.map(async (t) => {
          try {
            const data = await sportsDbFetch(
              `searchteams.php?t=${encodeURIComponent(t.name)}`
            );

            if (data?.teams?.[0]) {
              const team = data.teams[0];

              teams.push({
                id: team.idTeam,
                name: team.strTeam,
                badge: team.strBadge,
                fanart:
                  team.strTeamFanart1 ||
                  team.strTeamFanart2 ||
                  team.strTeamFanart3,
                banner: team.strTeamBanner,
                sport: team.strSport || t.sport,
                league: team.strLeague,
                country: team.strCountry,
                stadium: team.strStadium,
                formedYear: team.intFormedYear,
                color1: team.strColour1,
                color2: team.strColour2,
                description: team.strDescriptionEN,
                honours: t.honours,
              });
            }
          } catch (e) {
            /* ignore */
          }
        })
      );
      // Sort by honours descending (preserve rank)
      teams.sort((a, b) => b.honours - a.honours);
      setHofTeamData(teams);
    };

    fetchHofTeams();
  }, []);

  // Fetch highlights from TheSportsDB (past events with thumbnails)
  useEffect(() => {
    const HIGHLIGHT_LEAGUES = [4328, 4335, 4387]; // PL, La Liga, NBA
    const fetchHighlightsData = async () => {
      const allEvents = [];

      await Promise.allSettled(
        HIGHLIGHT_LEAGUES.map(async (leagueId) => {
          try {
            const data = await sportsDbFetch(
              `eventspastleague.php?id=${leagueId}`
            );

            (data?.events || []).forEach((ev) => {
              if (ev.strThumb && ev.strVideo) allEvents.push(ev);
            });
          } catch (e) {
            /* ignore */
          }
        })
      );
      allEvents.sort((a, b) => new Date(b.dateEvent) - new Date(a.dateEvent));
      setHighlights(allEvents.slice(0, 20));
    };

    fetchHighlightsData();
  }, []);

  // Format sports event for carousel
  const formatSportsEventForCarousel = (event) => {
    // v2 SportsFixture: { home: {...}, away: {...}, scores, status: { isLive, isFinished, isUpcoming } }
    // API-Sports legacy: { teams: { home, away }, scores, status: { short } }
    // TheSportsDB legacy: flat homeTeam/awayTeam strings
    const isV2 = !!(event.home && event.away && event.scores);
    const isAPISports = !isV2 && event.teams && event.scores && event.status;

    // Parse timestamp to get date and time - handle UTC properly
    let eventDate = null;

    if (isAPISports && event.date) {
      // API-Sports format - date is ISO string
      eventDate = new Date(event.date);
    } else if (event.strTimestamp) {
      // strTimestamp is ISO-8601 format (UTC) - it may or may not have Z suffix
      const timestampStr =
        event.strTimestamp.endsWith("Z") ||
        event.strTimestamp.includes("+") ||
        event.strTimestamp.includes("-")
          ? event.strTimestamp
          : `${event.strTimestamp}Z`;

      eventDate = new Date(timestampStr);
    } else if (event.timestamp != null) {
      if (typeof event.timestamp === "number") {
        // v2 API returns Long epoch — seconds if < 1e12, millis otherwise
        const ms =
          event.timestamp < 1e12 ? event.timestamp * 1000 : event.timestamp;

        eventDate = new Date(ms);
      } else {
        const ts = String(event.timestamp);
        const timestampStr =
          ts.endsWith("Z") || ts.includes("+") || ts.includes("-")
            ? ts
            : `${ts}Z`;

        eventDate = new Date(timestampStr);
      }
    } else if (event.dateEvent && event.strTime) {
      // Fallback: combine dateEvent and strTime as UTC
      const combinedDateTime = `${event.dateEvent}T${event.strTime}Z`;

      eventDate = new Date(combinedDateTime);
    }

    const now = new Date(); // User's local time

    // Check if event is live
    const isLive = isV2
      ? !!event.status?.isLive
      : isAPISports
        ? event.status?.short !== "NS" &&
          event.status?.short !== "TBD" &&
          event.status?.short !== "PST" &&
          event.status?.short !== "FT" &&
          event.status?.short !== "AOT" &&
          event.status?.short !== "AET"
        : (() => {
            if (!eventDate) return false;
            // Consider event live if it started within the last 4 hours
            const timeDiff = now - eventDate;
            const hoursDiff = timeDiff / (1000 * 60 * 60);

            return hoursDiff >= 0 && hoursDiff <= 4;
          })();

    const formattedDate = eventDate ? eventDate.toLocaleDateString() : "";
    const formattedTime = eventDate
      ? eventDate.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

    // Calculate time until game starts (for upcoming games)
    const getTimeUntilGame = () => {
      if (!eventDate) return "";
      const msUntilGame = eventDate - now;
      const minutesUntilGame = Math.floor(msUntilGame / (1000 * 60));
      const hoursUntilGame = Math.floor(minutesUntilGame / 60);

      if (hoursUntilGame > 0) {
        return `In ${hoursUntilGame}h`;
      } else if (minutesUntilGame > 0) {
        return `In ${minutesUntilGame}m`;
      } else {
        return "Starting soon";
      }
    };

    // Extract data based on format
    let homeTeam,
      awayTeam,
      homeTeamLogo,
      awayTeamLogo,
      homeScore,
      awayScore,
      leagueName,
      venueName;

    if (isV2) {
      homeTeam = event.home?.name;
      awayTeam = event.away?.name;
      homeTeamLogo = event.home?.logo;
      awayTeamLogo = event.away?.logo;
      // Android FixtureTeam.score is the live/current score; FixtureScores has the
      // half/full/quarter breakdown for finished games.
      const fs = event.scores || {};
      const homeBreakdown =
        fs.fulltime?.home ??
        fs.extratime?.home ??
        fs.penalty?.home ??
        fs.halftime?.home ??
        fs.quarter_4?.home ??
        fs.quarter_3?.home ??
        fs.quarter_2?.home ??
        fs.quarter_1?.home ??
        fs.overtime?.home;
      const awayBreakdown =
        fs.fulltime?.away ??
        fs.extratime?.away ??
        fs.penalty?.away ??
        fs.halftime?.away ??
        fs.quarter_4?.away ??
        fs.quarter_3?.away ??
        fs.quarter_2?.away ??
        fs.quarter_1?.away ??
        fs.overtime?.away;

      homeScore = getNumericValue(event.home?.score ?? homeBreakdown);
      awayScore = getNumericValue(event.away?.score ?? awayBreakdown);
      leagueName = event.league?.name || "";
      venueName =
        typeof event.venue === "string" ? event.venue : event.venue?.name || "";
    } else if (isAPISports) {
      homeTeam = event.teams.home.name;
      awayTeam = event.teams.away.name;
      homeTeamLogo = event.teams.home.logo;
      awayTeamLogo = event.teams.away.logo;
      // Use global helper to extract numeric scores
      homeScore = getNumericValue(event.scores?.home || event.goals?.home);
      awayScore = getNumericValue(event.scores?.away || event.goals?.away);
      leagueName = event.league?.name || "";
      venueName =
        typeof event.venue === "string" ? event.venue : event.venue?.name || "";
    } else {
      ({ homeTeam, awayTeam, homeTeamLogo, awayTeamLogo } = event);
      homeScore = getNumericValue(event.homeTeamScore);
      awayScore = getNumericValue(event.awayTeamScore);
      leagueName = event.league?.name || "";
      venueName =
        typeof event.venue === "string" ? event.venue : event.venue?.name || "";
    }

    // Format live events for display
    let liveEventsText = "";

    if (isAPISports && event.liveEvents && event.liveEvents.length > 0) {
      const recentEvents = event.liveEvents.slice(-3); // Show last 3 events

      liveEventsText = recentEvents
        .map((evt) => {
          if (evt.type === "Goal") {
            return `\u26BD ${evt.player} (${evt.time}')`;
          } else if (evt.detail === "Yellow Card") {
            return `\uD83D\uDFE8 ${evt.player} (${evt.time}')`;
          } else if (evt.detail === "Red Card") {
            return `\uD83D\uDFE5 ${evt.player} (${evt.time}')`;
          } else if (evt.type === "subst") {
            return `\uD83D\uDD04 ${evt.player} (${evt.time}')`;
          }

          return null;
        })
        .filter(Boolean)
        .join(" \u2022 ");
    }

    // For certain sports, always prefer backdrop over poster
    const preferBackdropSports = [
      "Ice Hockey",
      "Hockey",
      "Baseball",
      "Basketball",
      "American Football",
      "Football",
    ];
    const shouldUseBackdrop = preferBackdropSports.some(
      (sport) =>
        event.categoryName?.toLowerCase().includes(sport.toLowerCase()) ||
        event.sport?.toLowerCase().includes(sport.toLowerCase())
    );

    // Determine if game is upcoming (not started yet)
    const isUpcoming = isV2
      ? !!event.status?.isUpcoming
      : isAPISports
        ? event.status?.short === "NS" ||
          event.status?.short === "TBD" ||
          event.status?.short === "PST"
        : !isLive && eventDate && eventDate > now;

    // Build description with appropriate context
    const description = liveEventsText
      ? `${leagueName} \u2022 ${liveEventsText}`
      : isUpcoming
        ? `${leagueName} \u2022 ${formattedTime}`
        : `${leagueName} \u2022 ${formattedDate} ${formattedTime}`;

    // Determine what to show in the rating badge
    let ratingDisplay;

    if (isLive) {
      ratingDisplay = "LIVE";
    } else if (isUpcoming) {
      // For upcoming games, show time until game or game time
      const timeUntil = getTimeUntilGame();

      ratingDisplay = timeUntil || formattedTime;
    } else {
      // For finished games, show the score
      ratingDisplay = `${homeScore || 0} - ${awayScore || 0}`;
    }

    // For API-Sports format, backdropPath and posterPath are already set
    // For TheSportsDB format, use existing logic
    const backdropUrl = isAPISports
      ? event.backdropPath
      : event.backdropPath || event.backdrop || event.thumbnail;

    const posterUrl = isAPISports
      ? event.posterPath || event.backdropPath
      : shouldUseBackdrop
        ? event.backdropPath ||
          event.posterPath ||
          event.poster ||
          event.thumbnail
        : event.posterPath ||
          event.poster ||
          event.backdropPath ||
          event.thumbnail;

    return {
      id: event.mediaId || event.id,
      title: event.name || `${homeTeam} vs ${awayTeam}`,
      backdrop: backdropUrl,
      poster: posterUrl,
      description,
      rating: ratingDisplay,
      genre: event.categoryName || event.sport || "Sports",
      year: eventDate ? eventDate.getFullYear() : new Date().getFullYear(),
      type: "sports",
      sport: event.sport, // Preserve sport identifier (nba, nfl, ncaaf, etc.)
      name: event.name,
      metaName: event.metaName,
      homeTeam,
      awayTeam,
      homeTeamLogo,
      awayTeamLogo,
      homeTeamScore: homeScore,
      awayTeamScore: awayScore,
      venue: venueName,
      leagueLogo: event.league?.logoPath || event.league?.logo || "",
      league: leagueName,
      timestamp: event.timestamp,
      liveEvents: event.liveEvents || [],
    };
  };

  // Prepare carousel items from sports data - memoized so dependent useEffects/useMemos
  // don't refire on every render (was causing "Maximum update depth exceeded").
  const carouselItems = React.useMemo(
    () => sportsData.carousel.map(formatSportsEventForCarousel),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sportsData.carousel]
  );

  const liveGames = carouselItems
    .filter((g) => g.rating === "LIVE")
    .slice(0, 6);

  // Count of live games for the hero badge
  const liveCount = liveGames.length;

  // All games for the Live Now strip (live + upcoming) — personalized order
  const liveNowGames = React.useMemo(
    () => getPersonalizedLiveNowOrder(carouselItems),
    [carouselItems]
  );

  // Determine if this is a returning user with followed teams
  const isReturningUser = followedTeams.length > 0;

  // Personalized "Because you follow" rails — memoized to prevent re-renders
  const becauseYouFollowRails = React.useMemo(
    () =>
      followedTeams.length > 0 ? getBecauseYouFollowRails(carouselItems) : [],
    [carouselItems, followedTeams]
  );

  // Build dynamic status for each followed team (LIVE / upcoming / final / not playing)
  const followedTeamStatuses = React.useMemo(() => {
    if (followedTeams.length === 0) return [];
    // Search both the live carousel and the upcoming list — followed teams that
    // play later today won't appear in /v2/sports/home until kickoff.
    const upcomingFormatted = (sportsData.upcoming || [])
      .filter(Boolean)
      .map(formatSportsEventForCarousel);
    const allGames = [...carouselItems, ...upcomingFormatted];

    const norm = (s) => (typeof s === "string" ? s.trim().toLowerCase() : "");
    const teamMatches = (game, teamName) => {
      const target = norm(teamName);

      if (!target) return false;
      const home = norm(game.homeTeam);
      const away = norm(game.awayTeam);

      return (
        home === target ||
        away === target ||
        home.includes(target) ||
        away.includes(target) ||
        target.includes(home) ||
        target.includes(away)
      );
    };
    const teamIsHome = (game, teamName) => {
      const target = norm(teamName);
      const home = norm(game.homeTeam);

      return home === target || home.includes(target) || target.includes(home);
    };

    return followedTeams.map((teamName) => {
      const teamInfo = getTeamByName(teamName) || { name: teamName };
      const color = teamColors[teamName];
      const liveGame = allGames.find(
        (g) => g.rating === "LIVE" && teamMatches(g, teamName)
      );

      if (liveGame) {
        const isHome = teamIsHome(liveGame, teamName);

        return {
          teamName,
          teamInfo,
          color,
          status: "live",
          game: liveGame,
          isHome,
          opponent: isHome ? liveGame.awayTeam : liveGame.homeTeam,
          opponentLogo: isHome ? liveGame.awayTeamLogo : liveGame.homeTeamLogo,
          teamScore: isHome ? liveGame.homeTeamScore : liveGame.awayTeamScore,
          opponentScore: isHome
            ? liveGame.awayTeamScore
            : liveGame.homeTeamScore,
          period: liveGame.period,
          league: liveGame.league,
        };
      }
      // Check for upcoming game
      const upcomingGame = allGames.find(
        (g) =>
          g.rating !== "LIVE" &&
          (g.homeTeamScore == null || g.homeTeamScore === 0) &&
          (g.awayTeamScore == null || g.awayTeamScore === 0) &&
          teamMatches(g, teamName)
      );

      if (upcomingGame) {
        const isHome = teamIsHome(upcomingGame, teamName);

        return {
          teamName,
          teamInfo,
          color,
          status: "upcoming",
          game: upcomingGame,
          isHome,
          opponent: isHome ? upcomingGame.awayTeam : upcomingGame.homeTeam,
          opponentLogo: isHome
            ? upcomingGame.awayTeamLogo
            : upcomingGame.homeTeamLogo,
          startTime: upcomingGame.startTime || upcomingGame.time,
          league: upcomingGame.league,
        };
      }
      // Check for finished game
      const finishedGame = allGames.find(
        (g) =>
          g.rating !== "LIVE" &&
          (g.homeTeamScore != null || g.awayTeamScore != null) &&
          teamMatches(g, teamName)
      );

      if (finishedGame) {
        const isHome = teamIsHome(finishedGame, teamName);

        return {
          teamName,
          teamInfo,
          color,
          status: "final",
          game: finishedGame,
          isHome,
          opponent: isHome ? finishedGame.awayTeam : finishedGame.homeTeam,
          opponentLogo: isHome
            ? finishedGame.awayTeamLogo
            : finishedGame.homeTeamLogo,
          teamScore: isHome
            ? finishedGame.homeTeamScore
            : finishedGame.awayTeamScore,
          opponentScore: isHome
            ? finishedGame.awayTeamScore
            : finishedGame.homeTeamScore,
          league: finishedGame.league,
        };
      }

      // Not playing today
      return {
        teamName,
        teamInfo,
        color,
        status: "idle",
        league: teamInfo.league || teamInfo.sport || "",
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followedTeams, carouselItems, teamColors, sportsData.upcoming]);

  // Recommended teams (not yet followed) for Discover Teams section
  const recommendedTeams = React.useMemo(() => {
    const followedNames = new Set(followedTeams); // followedTeams is string[]
    const available = TEAMS_DATABASE.filter((t) => !followedNames.has(t.name));
    const shuffled = [...available].sort(() => Math.random() - 0.5);

    return shuffled.slice(0, 30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followedTeams.length]);

  // Multiple spotlight games for auto-rotation — only games involving the user's
  // followed teams. If none of their teams are playing, the spotlight stays empty
  // rather than falling back to random games.
  const spotlightGames = isReturningUser
    ? (() => {
        const followedSet = new Set(followedTeams);
        const myGames = carouselItems.filter(
          (g) => followedSet.has(g.homeTeam) || followedSet.has(g.awayTeam)
        );
        const personalized = getPersonalizedFeaturedGames(myGames, 5);

        return personalized.length > 0 ? personalized : myGames.slice(0, 5);
      })()
    : [];

  const [spotlightIndex, setSpotlightIndex] = useState(0);
  const [slidePhase, setSlidePhase] = useState("visible"); // "visible" | "sliding-out" | "sliding-in"
  const pendingIndexRef = useRef(null);
  // Animate slide transition when spotlight index changes
  const changeSpotlight = (nextIndex) => {
    if (nextIndex === spotlightIndex || slidePhase !== "visible") return;
    pendingIndexRef.current = nextIndex;
    setSlidePhase("sliding-out");
    setTimeout(() => {
      setSpotlightIndex(pendingIndexRef.current);
      setSlidePhase("sliding-in");
      setTimeout(() => {
        setSlidePhase("visible");
      }, 500);
    }, 400);
  };

  // Auto-rotate spotlight games every 8 seconds
  useEffect(() => {
    if (spotlightGames.length <= 1) return;
    const interval = setInterval(() => {
      const next = (spotlightIndex + 1) % spotlightGames.length;

      changeSpotlight(next);
    }, 8000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotlightGames.length, spotlightIndex, slidePhase]);

  const spotlightGame =
    spotlightGames.length > 0
      ? spotlightGames[spotlightIndex % spotlightGames.length]
      : null;

  // Sport-specific hero background image based on spotlight game
  const SPORT_HERO_BACKGROUNDS = {
    nba: "/sports-bg-basketball.png",
    basketball: "/sports-bg-basketball.png",
    nfl: "/sports-bg-nfl.png",
    ncaaf: "/sports-bg-nfl.png",
    "american football": "/sports-bg-nfl.png",
    nhl: "/sports-bg-nhl.png",
    "ice hockey": "/sports-bg-nhl.png",
    hockey: "/sports-bg-nhl.png",
    mlb: "/sports-bg-mlb.png",
    baseball: "/sports-bg-mlb.png",
    soccer: "/sports-bg-soccer.png",
    football: "/sports-bg-soccer.png",
    mls: "/sports-bg-soccer.png",
    "premier league": "/sports-bg-soccer.png",
    default: "/sports-bg-basketball.png",
  };

  const heroSportBg = (() => {
    const sport =
      spotlightGame?.sport?.toLowerCase() ||
      spotlightGame?.genre?.toLowerCase() ||
      liveGames[0]?.sport?.toLowerCase() ||
      liveGames[0]?.genre?.toLowerCase() ||
      "";

    return SPORT_HERO_BACKGROUNDS[sport] || SPORT_HERO_BACKGROUNDS.default;
  })();

  const [focusedGroup, setFocusedGroup] = useState("hero"); // "hero" | "sidebar" | "pills" | "livenow"
  const [focusedHeroBtn, setFocusedHeroBtn] = useState(0); // 0 = Watch Now / Pick Teams, 1 = Edit Teams / Browse Leagues, 2 = Leagues
  const [focusedSidebarCard, setFocusedSidebarCard] = useState(0); // 0-3 (max 4 sidebar cards)
  const [focusedLiveCard, setFocusedLiveCard] = useState(0);
  const liveNowScrollRef = useRef(null);
  // Context menu state
  const [contextMenu, setContextMenu] = useState(null); // { x, y, game }

  // Context menu handler (right-click on desktop)
  const handleGameContextMenu = (e, game) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, game });
  };

  // Long-press handler for TV (hold Enter/OK on remote)
  const longPressRef = useRef(null);
  const longPressDidFire = useRef(false);

  const handleCardKeyDown = (e, game) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    if (longPressRef.current) return; // already held
    longPressDidFire.current = false;
    longPressRef.current = setTimeout(() => {
      longPressDidFire.current = true;
      // Position context menu at center of the card
      const rect = e.currentTarget.getBoundingClientRect();

      setContextMenu({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
        game,
      });
    }, 600);
  };

  const handleCardKeyUp = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    clearTimeout(longPressRef.current);
    longPressRef.current = null;
    // If long press fired, prevent the normal click from opening
    if (longPressDidFire.current) {
      e.preventDefault();
      e.stopPropagation();
      longPressDidFire.current = false;
    }
  };

  const dismissContextMenu = () => setContextMenu(null);

  // Dismiss context menu on click outside or Escape
  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = () => dismissContextMenu();
    const handleKey = (e) => {
      if (e.key === "Escape") dismissContextMenu();
    };

    window.addEventListener("click", handleClick);
    window.addEventListener("keydown", handleKey);

    return () => {
      window.removeEventListener("click", handleClick);
      window.removeEventListener("keydown", handleKey);
    };
  }, [contextMenu]);

  // Build event state object for navigation (reusable helper)
  const buildEventState = (game) => ({
    event: {
      id: game.id,
      mediaId: game.mediaId,
      idEvent: game.idEvent,
      name: game.title || game.name,
      sport: game.sport,
      strHomeTeam: game.homeTeam,
      strAwayTeam: game.awayTeam,
      strHomeTeamBadge: game.homeTeamLogo,
      strAwayTeamBadge: game.awayTeamLogo,
      homeTeamScore: game.homeTeamScore,
      awayTeamScore: game.awayTeamScore,
      strLeague: game.league,
      strLeagueBadge: game.leagueLogo,
      timestamp: game.timestamp,
      rating: game.rating,
      status: game.status,
      period: game.period,
      venue: game.venue,
      season: game.season,
      liveEvents: game.liveEvents,
    },
  });

  // Navigate to event detail page
  const navigateToEvent = (game) => {
    navigate(`/web-player/sports/event/${game.id || game.mediaId || "live"}`, {
      state: buildEventState(game),
    });
  };

  // DPAD Focus Management
  useEffect(() => {
    const maxHeroBtn = isReturningUser ? 2 : 1;
    const maxSidebarCard = Math.min(liveGames.length, 4) - 1;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowDown":
          if (focusedGroup === "hero") {
            setFocusedGroup("pills");
          } else if (focusedGroup === "sidebar") {
            if (focusedSidebarCard < maxSidebarCard) {
              setFocusedSidebarCard((prev) => prev + 1);
            } else {
              setFocusedGroup("pills");
            }
          } else if (focusedGroup === "pills") {
            setFocusedGroup("livenow");
            setFocusedLiveCard(0);
          } else if (focusedGroup === "livenow") {
            // Already at bottom
          }
          break;
        case "ArrowUp":
          if (focusedGroup === "livenow") {
            setFocusedGroup("pills");
          } else if (focusedGroup === "pills") {
            setFocusedGroup("hero");
          } else if (focusedGroup === "sidebar") {
            if (focusedSidebarCard > 0) {
              setFocusedSidebarCard((prev) => prev - 1);
            } else {
              setFocusedGroup("hero");
            }
          }
          break;
        case "ArrowLeft":
          if (focusedGroup === "hero") {
            setFocusedHeroBtn((prev) => Math.max(0, prev - 1));
          } else if (focusedGroup === "sidebar") {
            setFocusedGroup("hero");
          } else if (focusedGroup === "livenow") {
            setFocusedLiveCard((prev) => Math.max(0, prev - 1));
          }
          break;
        case "ArrowRight":
          if (focusedGroup === "hero") {
            if (focusedHeroBtn >= maxHeroBtn && maxSidebarCard >= 0) {
              // Past last hero button, jump to sidebar
              setFocusedGroup("sidebar");
              setFocusedSidebarCard(0);
            } else {
              setFocusedHeroBtn((prev) => Math.min(maxHeroBtn, prev + 1));
            }
          } else if (focusedGroup === "livenow") {
            setFocusedLiveCard((prev) =>
              Math.min(liveNowGames.length - 1, prev + 1)
            );
          }
          break;
        case "Enter":
          if (focusedGroup === "hero") {
            if (isReturningUser) {
              if (focusedHeroBtn === 0 && spotlightGame) {
                navigateToEvent(spotlightGame);
              } else if (focusedHeroBtn === 1) {
                setShowTeamPicker(true);
              } else if (focusedHeroBtn === 2) {
                navigate("/web-player/sports/leagues");
              }
            } else {
              if (focusedHeroBtn === 0) {
                setShowTeamPicker(true);
              } else {
                navigate("/web-player/sports/leagues");
              }
            }
          } else if (focusedGroup === "sidebar") {
            const sidebarGame = liveGames[focusedSidebarCard];

            if (sidebarGame) {
              navigateToEvent(sidebarGame);
            }
          } else if (focusedGroup === "livenow") {
            const liveGame = liveNowGames[focusedLiveCard];

            if (liveGame) {
              navigateToEvent(liveGame);
            }
          }
          break;
        case "Escape":
          // Close team picker if open
          if (showTeamPicker) {
            setShowTeamPicker(false);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    focusedGroup,
    focusedHeroBtn,
    focusedSidebarCard,
    focusedLiveCard,
    liveNowGames.length,
    liveGames.length,
    navigate,
    isReturningUser,
    spotlightGame,
    showTeamPicker,
  ]);

  // Auto-scroll focused live card into view
  useEffect(() => {
    if (focusedGroup === "livenow" && liveNowScrollRef.current) {
      const cards =
        liveNowScrollRef.current.querySelectorAll("[data-live-card]");

      if (cards[focusedLiveCard]) {
        cards[focusedLiveCard].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [focusedGroup, focusedLiveCard]);

  // Team search handler
  useEffect(() => {
    if (teamSearchQuery.length >= 2) {
      const results = searchTeams(teamSearchQuery, 20);

      setTeamSearchResults(results);
    } else {
      setTeamSearchResults([]);
    }
  }, [teamSearchQuery]);

  // Focus the search input when overlay opens
  useEffect(() => {
    if (showTeamPicker && teamSearchInputRef.current) {
      setTimeout(() => teamSearchInputRef.current?.focus(), 100);
    }
  }, [showTeamPicker]);

  // Handle team toggle in the picker
  const handleToggleTeam = (team) => {
    const updatedTeams = toggleFavoriteTeam(team);

    setFollowedTeams([...updatedTeams]);
  };

  // Get top 3 personalized games based on followed teams
  const getTopGamesForUser = () => {
    if (followedTeams.length === 0) return [];

    return getPersonalizedFeaturedGames(carouselItems, 3);
  };

  // Get suggested teams based on same leagues as followed teams (memoized to avoid re-shuffle on every render)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const suggestedTeams = React.useMemo(() => {
    if (followedTeams.length === 0) return [];
    const followedLeagues = new Set();

    TEAMS_DATABASE.forEach((t) => {
      if (followedTeams.includes(t.name)) followedLeagues.add(t.league);
    });
    const candidates = TEAMS_DATABASE.filter(
      (t) => followedLeagues.has(t.league) && !followedTeams.includes(t.name)
    );
    const shuffled = [...candidates].sort(() => Math.random() - 0.5);

    return shuffled.slice(0, 8);
  }, [followedTeams]);

  // ---- SUPERFAN MODE: find the live game matching superfanTeam ----
  const superfanGame = React.useMemo(() => {
    if (!superfanTeam) return null;

    return (
      carouselItems.find(
        (g) =>
          g.rating === "LIVE" &&
          (g.homeTeam === superfanTeam || g.awayTeam === superfanTeam)
      ) || null
    );
  }, [superfanTeam, carouselItems]);

  // Superfan real-time event feed polling (every 30s)
  useEffect(() => {
    if (!superfanGame || !superfanTeam) {
      setSuperfanEvents([]);

      return;
    }
    const fetchEvents = async () => {
      try {
        const mediaId =
          superfanGame?.idEvent || superfanGame?.id || superfanGame?.mediaId;

        if (!mediaId) return;
        const res = await apiService.webPlayerRequest(
          `/v2/sports/events/live/${mediaId}`
        );
        const events =
          res?.data?.events ||
          res?.events ||
          res?.data?.timeline ||
          res?.timeline ||
          [];

        setSuperfanEvents(events);
        // Player callout for latest event involving followed team
        if (events.length > 0) {
          const latest = events[events.length - 1];
          const playerName =
            latest.player || latest.detail || latest.assist || null;

          if (playerName) {
            setSuperfanCallout({
              player: playerName,
              event:
                latest.type ||
                latest.detail ||
                `${latest.time?.elapsed}'` ||
                "",
              team: latest.team?.name || superfanTeam,
            });
            if (superfanCalloutTimer.current)
              clearTimeout(superfanCalloutTimer.current);
            superfanCalloutTimer.current = setTimeout(
              () => setSuperfanCallout(null),
              5000
            );
          }
        }
      } catch {
        /* silent */
      }
    };

    fetchEvents();
    const interval = setInterval(fetchEvents, 30000);

    return () => {
      clearInterval(interval);
      if (superfanCalloutTimer.current)
        clearTimeout(superfanCalloutTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superfanGame?.id, superfanTeam]);

  // Superfan team-branded colors map
  const TEAM_COLORS = {
    // NBA
    "Los Angeles Lakers": { primary: "#552583", secondary: "#FDB927" },
    "Boston Celtics": { primary: "#007A33", secondary: "#BA9653" },
    "Golden State Warriors": { primary: "#1D428A", secondary: "#FFC72C" },
    "Chicago Bulls": { primary: "#CE1141", secondary: "#000000" },
    "Miami Heat": { primary: "#98002E", secondary: "#F9A01B" },
    // NFL
    "Kansas City Chiefs": { primary: "#E31837", secondary: "#FFB81C" },
    "San Francisco 49ers": { primary: "#AA0000", secondary: "#B3995D" },
    "Dallas Cowboys": { primary: "#003594", secondary: "#869397" },
    // Soccer
    Arsenal: { primary: "#EF0107", secondary: "#FFFFFF" },
    Chelsea: { primary: "#034694", secondary: "#DBA111" },
    "Manchester United": { primary: "#DA291C", secondary: "#FBE122" },
    "Real Madrid": { primary: "#FEBE10", secondary: "#00529F" },
    Barcelona: { primary: "#A50044", secondary: "#004D98" },
    // NHL
    "New York Rangers": { primary: "#0038A8", secondary: "#CE1126" },
    "Boston Bruins": { primary: "#FFB81C", secondary: "#000000" },
    // Default
    default: { primary: "#3b82f6", secondary: "#8b5cf6" },
  };

  const getTeamColors = (teamName) =>
    TEAM_COLORS[teamName] || TEAM_COLORS.default;

  // ---- AI GAME RECAP: generate template-based recaps for finished games ----
  useEffect(() => {
    if (followedTeams.length === 0) return;
    const finishedGames = carouselItems.filter(
      (g) =>
        g.rating !== "LIVE" &&
        (g.homeTeamScore != null || g.awayTeamScore != null) &&
        (followedTeams.includes(g.homeTeam) ||
          followedTeams.includes(g.awayTeam))
    );

    const recaps = finishedGames.slice(0, 3).map((game) => {
      const homeScore = game.homeTeamScore ?? 0;
      const awayScore = game.awayTeamScore ?? 0;
      const winner =
        homeScore > awayScore
          ? game.homeTeam
          : awayScore > homeScore
            ? game.awayTeam
            : null;
      const loser =
        homeScore > awayScore
          ? game.awayTeam
          : awayScore > homeScore
            ? game.homeTeam
            : null;
      const winScore = Math.max(homeScore, awayScore);
      const loseScore = Math.min(homeScore, awayScore);
      const diff = winScore - loseScore;

      let headline, detail;

      if (!winner) {
        headline = `${game.homeTeam} and ${game.awayTeam} draw ${homeScore}-${awayScore}`;
        detail = `A closely contested ${game.league || "game"} match ends level.`;
      } else if (diff >= 15) {
        headline = `${winner} dominate ${loser} ${winScore}-${loseScore}`;
        detail = `A commanding performance in ${game.league || "the league"}.`;
      } else if (diff >= 5) {
        headline = `${winner} defeat ${loser} ${winScore}-${loseScore}`;
        detail = `A solid win in their ${game.league || "league"} matchup.`;
      } else {
        headline = `${winner} edge ${loser} ${winScore}-${loseScore}`;
        detail = `A tight contest decided by just ${diff} point${diff !== 1 ? "s" : ""}.`;
      }

      return {
        id: game.id || `recap-${game.homeTeam}-${game.awayTeam}`,
        game,
        headline,
        detail,
        league: game.league,
        sport: game.sport || game.genre,
        homeTeam: game.homeTeam,
        awayTeam: game.awayTeam,
        homeScore,
        awayScore,
        homeLogo: game.homeTeamLogo,
        awayLogo: game.awayTeamLogo,
      };
    });

    setGameRecaps(recaps);
  }, [carouselItems, followedTeams]);

  // ---- HEARTBEAT MODE: simulate scoring snapshots + pulse detection ----
  useEffect(() => {
    if (!heartbeatMode) return;
    // Initialize heartbeat data from current scores
    const initData = {};
    const allGames = liveNowGames || [];

    allGames.forEach((g) => {
      const id = g.id || `${g.homeTeam}-${g.awayTeam}`;
      const home = g.homeTeamScore ?? 0;
      const away = g.awayTeamScore ?? 0;

      if (!heartbeatData[id]) {
        // Generate fake historical sparkline (10 data points)
        const points = [];

        for (let i = 0; i < 10; i++) {
          const hVariance = Math.floor(
            home * (0.3 + (0.7 * i) / 10) + Math.random() * 2
          );
          const aVariance = Math.floor(
            away * (0.3 + (0.7 * i) / 10) + Math.random() * 2
          );

          points.push({ home: hVariance, away: aVariance });
        }
        points.push({ home, away }); // current score as last point
        initData[id] = points;
      }
    });
    if (Object.keys(initData).length > 0) {
      setHeartbeatData((prev) => ({ ...prev, ...initData }));
    }

    // Poll for score changes every 15s
    const interval = setInterval(() => {
      const games = liveNowGames || [];
      const newPulses = {};

      setHeartbeatData((prev) => {
        const next = { ...prev };

        games.forEach((g) => {
          const id = g.id || `${g.homeTeam}-${g.awayTeam}`;
          const arr = next[id] || [];
          const lastPoint = arr[arr.length - 1] || { home: 0, away: 0 };
          const home = g.homeTeamScore ?? 0;
          const away = g.awayTeamScore ?? 0;

          if (home !== lastPoint.home) newPulses[id] = "home";
          if (away !== lastPoint.away) newPulses[id] = "away";
          next[id] = [...arr.slice(-10), { home, away }];
        });

        return next;
      });
      if (Object.keys(newPulses).length > 0) {
        setHeartbeatPulse(newPulses);
        setTimeout(() => setHeartbeatPulse({}), 1200);
      }
    }, 15000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heartbeatMode, liveNowGames.length]);

  // Mini sparkline SVG renderer
  const renderSparkline = (dataPoints, teamKey, color) => {
    if (!dataPoints || dataPoints.length < 2) return null;
    const values = dataPoints.map((p) => p[teamKey]);
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const range = max - min || 1;
    const w = 60;
    const h = 20;
    const points = values
      .map((v, i) => {
        const x = (i / (values.length - 1)) * w;
        const y = h - ((v - min) / range) * h;

        return `${x},${y}`;
      })
      .join(" ");

    return (
      <svg
        className="flex-shrink-0"
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        width={w}
      >
        <polyline
          fill="none"
          points={points}
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
    );
  };

  return (
    <>
      {/* Main Sports Test Page */}
      <div className="min-h-screen">
        {/* Left Menu */}
        <LeftMenu
          activeMenuItem="sportstest"
          playlist={playlist}
          profile={profile}
        />

        {/* Mobile Menu */}
        <MobileMenu />

        {/* Main Content */}
        <div className="relative z-10">
          {/* ============================================ */}
          {/* SUPERFAN MODE: Single-game command center     */}
          {/* ============================================ */}
          {superfanGame && superfanTeam && (
            <div className="relative min-h-[80vh] flex items-stretch overflow-hidden">
              {/* Superfan branded background */}
              <div className="absolute inset-0">
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(135deg, ${getTeamColors(superfanTeam).primary}22 0%, #0a0e1a 40%, ${getTeamColors(superfanTeam).secondary}15 100%)`,
                  }}
                />
                <img
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-20"
                  src={heroSportBg}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a0e1a]/90 via-[#0a0e1a]/50 to-[#0a0e1a]/80" />
                {/* Team color glow */}
                <div
                  className="absolute top-0 left-1/4 w-[800px] h-[800px] rounded-full opacity-[0.08]"
                  style={{
                    background: `radial-gradient(circle, ${getTeamColors(superfanTeam).primary} 0%, transparent 70%)`,
                  }}
                />
                <div
                  className="absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full opacity-[0.06]"
                  style={{
                    background: `radial-gradient(circle, ${getTeamColors(superfanTeam).secondary} 0%, transparent 70%)`,
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
              </div>

              {/* Superfan header bar */}
              <div className="absolute top-4 left-0 right-0 z-20 px-8 md:px-16 lg:px-24 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-2 h-2 rounded-full superfan-pulse"
                    style={{
                      backgroundColor: getTeamColors(superfanTeam).primary,
                    }}
                  />
                  <span
                    className="text-xs font-bold uppercase tracking-widest"
                    style={{
                      color: getTeamColors(superfanTeam).primary,
                    }}
                  >
                    Superfan Mode
                  </span>
                  <span className="text-white/30 text-xs">
                    &middot; {superfanTeam}
                  </span>
                </div>
                <button
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.06] border border-white/[0.1] text-white/40 text-xs hover:bg-red-500/15 hover:border-red-500/30 hover:text-red-300 transition-all duration-200"
                  onClick={() => toggleSuperfan(superfanTeam)}
                >
                  <i className="fas fa-times text-[10px]" />
                  Exit Superfan
                </button>
              </div>

              {/* Superfan content: Game center (left 65%) + Event feed (right 35%) */}
              <div className="relative z-10 w-full px-8 md:px-16 lg:px-24 py-20 flex gap-8 max-w-[1600px] mx-auto">
                {/* Left: Game center */}
                <div className="flex-1 flex flex-col justify-center">
                  {/* Live badge */}
                  <div className="flex items-center gap-2 mb-6">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500 live-pulse" />
                    <span className="text-red-400 text-sm font-bold uppercase tracking-wider">
                      Live Now
                    </span>
                    {superfanGame.period && (
                      <span className="text-white/40 text-sm ml-2">
                        {superfanGame.period}
                      </span>
                    )}
                  </div>

                  {/* Large matchup */}
                  <div className="flex items-center gap-8 mb-8">
                    {/* Away */}
                    <div className="flex flex-col items-center gap-3">
                      {superfanGame.awayTeamLogo && (
                        <img
                          alt=""
                          className={`w-24 h-24 object-contain drop-shadow-2xl ${superfanGame.awayTeam === superfanTeam ? "ring-2 ring-offset-4 ring-offset-transparent rounded-full" : ""}`}
                          src={superfanGame.awayTeamLogo}
                          style={
                            superfanGame.awayTeam === superfanTeam
                              ? {
                                  ringColor:
                                    getTeamColors(superfanTeam).primary,
                                }
                              : {}
                          }
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      )}
                      <span
                        className={`text-base font-medium ${superfanGame.awayTeam === superfanTeam ? "text-white" : "text-white/60"}`}
                      >
                        {superfanGame.awayTeam}
                      </span>
                    </div>

                    {/* Score */}
                    <div className="flex flex-col items-center">
                      <div className="text-5xl md:text-7xl font-extrabold text-white tabular-nums tracking-tight">
                        {spoilerFree ? (
                          <span className="text-white/30">VS</span>
                        ) : (
                          <>
                            {superfanGame.awayTeamScore ?? 0}
                            <span className="text-white/20 mx-3">-</span>
                            {superfanGame.homeTeamScore ?? 0}
                          </>
                        )}
                      </div>
                      <span className="text-sm text-white/40 mt-2">
                        {superfanGame.league}
                      </span>
                    </div>

                    {/* Home */}
                    <div className="flex flex-col items-center gap-3">
                      {superfanGame.homeTeamLogo && (
                        <img
                          alt=""
                          className={`w-24 h-24 object-contain drop-shadow-2xl ${superfanGame.homeTeam === superfanTeam ? "ring-2 ring-offset-4 ring-offset-transparent rounded-full" : ""}`}
                          src={superfanGame.homeTeamLogo}
                          style={
                            superfanGame.homeTeam === superfanTeam
                              ? {
                                  ringColor:
                                    getTeamColors(superfanTeam).primary,
                                }
                              : {}
                          }
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      )}
                      <span
                        className={`text-base font-medium ${superfanGame.homeTeam === superfanTeam ? "text-white" : "text-white/60"}`}
                      >
                        {superfanGame.homeTeam}
                      </span>
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex gap-3">
                    <button
                      className="px-8 py-3.5 rounded-xl font-semibold text-base transition-all duration-200"
                      style={{
                        backgroundColor: `${getTeamColors(superfanTeam).primary}cc`,
                        color: "#fff",
                      }}
                      onClick={() => navigateToEvent(superfanGame)}
                    >
                      <i className="fas fa-play mr-2" />
                      Watch Now
                    </button>
                    <button
                      className="px-6 py-3.5 rounded-xl font-semibold text-sm bg-white/5 text-white/60 border border-white/15 hover:bg-white/10 hover:text-white/80 transition-all duration-200"
                      onClick={() => navigateToEvent(superfanGame)}
                    >
                      <i className="fas fa-chart-bar mr-2" />
                      Full Stats
                    </button>
                  </div>
                </div>

                {/* Right: Real-time event feed */}
                <div className="w-[320px] flex-shrink-0 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="fas fa-stream text-xs text-white/40" />
                    <span className="text-xs font-bold text-white/40 uppercase tracking-wider">
                      Live Feed
                    </span>
                  </div>
                  <div className="flex-1 bg-white/[0.03] rounded-xl border border-white/[0.06] p-4 overflow-y-auto max-h-[350px] space-y-2 scrollbar-thin">
                    {superfanEvents.length > 0 ? (
                      [...superfanEvents].reverse().map((evt, i) => {
                        const timeStr =
                          evt.time?.elapsed || evt.elapsed || evt.minute || "";
                        const type = (
                          evt.type ||
                          evt.detail ||
                          ""
                        ).toLowerCase();
                        const isGoal = type.includes("goal");
                        const isCard =
                          type.includes("card") || type.includes("yellow");

                        return (
                          <div
                            className={`flex items-start gap-2.5 py-2 px-2 rounded-lg ${isGoal ? "bg-green-500/10 border border-green-500/20" : isCard ? "bg-yellow-500/10 border border-yellow-500/20" : "bg-white/[0.02]"}`}
                            key={i}
                          >
                            <span className="text-[10px] font-mono text-white/30 w-8 flex-shrink-0 pt-0.5">
                              {timeStr}
                              {timeStr ? "'" : ""}
                            </span>
                            <div className="min-w-0">
                              <span
                                className={`text-xs font-medium ${isGoal ? "text-green-400" : isCard ? "text-yellow-400" : "text-white/60"}`}
                              >
                                {evt.player ||
                                  evt.detail ||
                                  evt.type ||
                                  "Event"}
                              </span>
                              {evt.team?.name && (
                                <span className="text-[10px] text-white/25 ml-1.5">
                                  {evt.team.name}
                                </span>
                              )}
                              {evt.assist && (
                                <span className="text-[10px] text-white/20 block">
                                  Assist: {evt.assist}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8">
                        <i className="fas fa-satellite-dish text-2xl text-white/10 mb-2" />
                        <p className="text-xs text-white/25">
                          Waiting for events...
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Superfan player callout popup */}
              {superfanCallout && (
                <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-30 superfan-callout-enter">
                  <div
                    className="flex items-center gap-3 px-5 py-3 rounded-2xl border shadow-2xl backdrop-blur-lg"
                    style={{
                      backgroundColor: `${getTeamColors(superfanTeam).primary}30`,
                      borderColor: `${getTeamColors(superfanTeam).primary}50`,
                    }}
                  >
                    <i className="fas fa-bolt text-yellow-400" />
                    <div>
                      <span className="text-sm font-bold text-white">
                        {superfanCallout.player}
                      </span>
                      <span className="text-xs text-white/50 ml-2">
                        {superfanCallout.event}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============================================ */}
          {/* SECTION 1: CINEMATIC SPLIT HERO              */}
          {/* ============================================ */}
          {/* Hide normal hero when superfan mode is active with a live game */}
          <div
            className={`relative min-h-[65vh] flex items-center overflow-hidden ${superfanGame && superfanTeam ? "hidden" : ""}`}
          >
            {/* Hero Background - Stadium gradient */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0a0e1a] via-[#0d1225] to-[#0a0a12]" />
              {/* Sport-specific background image */}
              <img
                alt=""
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out"
                src={heroSportBg}
                style={{
                  opacity: slidePhase === "sliding-out" ? 0 : 0.35,
                }}
              />
              {/* Dark overlay on top of sport image */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0a0e1a]/80 via-[#0a0e1a]/40 to-[#0a0e1a]/70" />
              {/* Stadium light glow effects */}
              <div
                className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-[0.07]"
                style={{
                  background:
                    "radial-gradient(circle, #3b82f6 0%, transparent 70%)",
                }}
              />
              <div
                className="absolute top-20 right-1/4 w-[400px] h-[400px] rounded-full opacity-[0.05]"
                style={{
                  background:
                    "radial-gradient(circle, #8b5cf6 0%, transparent 70%)",
                }}
              />
              {/* Subtle grid pattern overlay */}
              <div
                className="absolute inset-0 opacity-[0.03]"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
                  backgroundSize: "60px 60px",
                }}
              />
              {/* Bottom fade inside hero — blends with outer gradient */}
              <div
                className="absolute bottom-0 left-0 right-0 pointer-events-none"
                style={{
                  height: "50%",
                  background:
                    "linear-gradient(to bottom, transparent 0%, rgba(10,14,26,1) 100%)",
                }}
              />
            </div>

            {/* Welcome greeting + spoiler toggle */}
            <div className="absolute top-4 left-0 right-0 z-20 px-8 md:px-16 lg:px-24 flex items-center justify-between hero-fade-in">
              {isReturningUser ? (
                <p className="text-white/40 text-sm font-medium">
                  Welcome back,{" "}
                  <span className="text-white/70">
                    {profile?.name || "Sports Fan"}
                  </span>
                </p>
              ) : (
                <div />
              )}
              <button
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                  spoilerFree
                    ? "bg-purple-500/20 border border-purple-500/40 text-purple-300"
                    : "bg-white/[0.06] border border-white/[0.1] text-white/40 hover:bg-white/[0.1] hover:text-white/60"
                }`}
                title={spoilerFree ? "Show scores" : "Hide scores"}
                onClick={toggleSpoilerFree}
              >
                <i
                  className={`fas ${spoilerFree ? "fa-eye-slash" : "fa-eye"} text-[10px]`}
                />
                {spoilerFree ? "Spoiler-Free" : "Scores"}
              </button>
            </div>

            {/* Hero Content */}
            <div className="relative z-10 w-full px-8 md:px-16 lg:px-24 py-16">
              <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12 max-w-[1600px] mx-auto">
                {/* Left Column (60%) - Conditional: Spotlight vs Welcome */}
                {isReturningUser && spotlightGame ? (
                  /* ---- SPOTLIGHT HERO (Returning User) ---- */
                  <div
                    className={`flex-1 lg:max-w-[60%] spotlight-slide ${slidePhase === "sliding-out" ? "spotlight-slide-out" : slidePhase === "sliding-in" ? "spotlight-slide-in" : ""}`}
                  >
                    {/* Status badge */}
                    {spotlightGame.rating === "LIVE" ? (
                      <button
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/15 border border-red-500/30 mb-6 cursor-pointer hover:bg-red-500/25 hover:border-red-500/50 transition-all duration-200"
                        onClick={() => {
                          const el = document.querySelector("#live-now-strip");

                          if (el)
                            el.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                        }}
                      >
                        <span className="w-2 h-2 rounded-full bg-red-500 live-pulse" />
                        <span className="text-red-400 text-xs font-bold uppercase tracking-wider">
                          Live Now
                        </span>
                      </button>
                    ) : (
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/15 border border-blue-500/30 mb-6">
                        <i className="fas fa-star text-blue-400 text-xs" />
                        <span className="text-blue-400 text-xs font-bold uppercase tracking-wider">
                          Your Top Game
                        </span>
                      </div>
                    )}

                    {/* Game Matchup - Large Team Display */}
                    <div className="flex items-center gap-6 md:gap-8 mb-6">
                      {/* Away Team */}
                      <div className="flex flex-col items-center gap-2">
                        {spotlightGame.awayTeamLogo ? (
                          <img
                            alt=""
                            className="w-16 h-16 md:w-20 md:h-20 object-contain drop-shadow-lg"
                            src={spotlightGame.awayTeamLogo}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/10 flex items-center justify-center">
                            <span className="text-2xl font-bold text-white/40">
                              {spotlightGame.awayTeam?.charAt(0) || "?"}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm md:text-base text-white/80 font-medium text-center">
                            {spotlightGame.awayTeam}
                          </span>
                          {followedTeams.includes(spotlightGame.awayTeam) && (
                            <i className="fas fa-heart text-blue-400/60 text-[10px]" />
                          )}
                        </div>
                      </div>

                      {/* Score / VS */}
                      <div className="flex flex-col items-center">
                        {spoilerFree ? (
                          <div className="text-2xl md:text-3xl font-bold text-white/30">
                            VS
                          </div>
                        ) : spotlightGame.rating === "LIVE" ||
                          (spotlightGame.homeTeamScore != null &&
                            spotlightGame.awayTeamScore != null) ? (
                          <div className="text-3xl md:text-5xl font-extrabold text-white tabular-nums tracking-tight">
                            {spotlightGame.awayTeamScore ?? 0}
                            <span className="text-white/30 mx-2">-</span>
                            {spotlightGame.homeTeamScore ?? 0}
                          </div>
                        ) : (
                          <div className="text-2xl md:text-3xl font-bold text-white/30">
                            VS
                          </div>
                        )}
                        <span className="text-xs text-white/40 mt-1 font-medium">
                          {spotlightGame.league}
                        </span>
                      </div>

                      {/* Home Team */}
                      <div className="flex flex-col items-center gap-2">
                        {spotlightGame.homeTeamLogo ? (
                          <img
                            alt=""
                            className="w-16 h-16 md:w-20 md:h-20 object-contain drop-shadow-lg"
                            src={spotlightGame.homeTeamLogo}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/10 flex items-center justify-center">
                            <span className="text-2xl font-bold text-white/40">
                              {spotlightGame.homeTeam?.charAt(0) || "?"}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm md:text-base text-white/80 font-medium text-center">
                            {spotlightGame.homeTeam}
                          </span>
                          {followedTeams.includes(spotlightGame.homeTeam) && (
                            <i className="fas fa-heart text-blue-400/60 text-[10px]" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* CTA Buttons */}
                    <div className="flex flex-wrap gap-3 mb-6">
                      <button
                        className={`px-8 py-3.5 rounded-xl font-semibold text-base transition-all duration-200 ${
                          focusedGroup === "hero" && focusedHeroBtn === 0
                            ? "bg-blue-500 text-white shadow-lg shadow-blue-500/30 scale-105 ring-2 ring-blue-400/50"
                            : "bg-blue-600 text-white hover:bg-blue-500 hover:shadow-lg hover:shadow-blue-500/25"
                        }`}
                        onClick={() => {
                          navigate("/web-player/sports/find-channel", {
                            state: buildEventState(spotlightGame),
                          });
                        }}
                        onFocus={() => {
                          setFocusedGroup("hero");
                          setFocusedHeroBtn(0);
                        }}
                      >
                        <i className="fas fa-satellite-dish mr-2" />
                        Find Channel
                      </button>
                      <button
                        className={`px-6 py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 ${
                          focusedGroup === "hero" && focusedHeroBtn === 1
                            ? "bg-white/15 text-white border border-white/40 scale-105 ring-2 ring-white/30"
                            : "bg-white/5 text-white/60 border border-white/15 hover:bg-white/10 hover:text-white/80"
                        }`}
                        onClick={() => navigate("/web-player/sports/leagues")}
                        onFocus={() => {
                          setFocusedGroup("hero");
                          setFocusedHeroBtn(1);
                        }}
                      >
                        <i className="fas fa-trophy mr-2" />
                        Leagues
                      </button>
                      <button
                        className={`px-6 py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 ${
                          focusedGroup === "hero" && focusedHeroBtn === 2
                            ? "bg-white/15 text-white border border-white/40 scale-105 ring-2 ring-white/30"
                            : "bg-white/5 text-white/60 border border-white/15 hover:bg-white/10 hover:text-white/80"
                        }`}
                        onClick={() => navigateToEvent(spotlightGame)}
                        onFocus={() => {
                          setFocusedGroup("hero");
                          setFocusedHeroBtn(2);
                        }}
                      >
                        <i className="fas fa-info-circle mr-2" />
                        More Info
                      </button>
                    </div>

                    {/* Spotlight Game Dots */}
                    {spotlightGames.length > 1 && (
                      <div className="flex items-center gap-2">
                        {spotlightGames.map((g, i) => (
                          <button
                            className={`transition-all duration-300 rounded-full ${
                              i === spotlightIndex % spotlightGames.length
                                ? "w-6 h-2 bg-blue-500"
                                : "w-2 h-2 bg-white/25 hover:bg-white/40"
                            }`}
                            key={g.id || i}
                            onClick={() => changeSpotlight(i)}
                          />
                        ))}
                      </div>
                    )}

                    {/* Followed Teams Chips — 3 rows max */}
                    <div className="flex flex-wrap gap-2 mt-10 max-h-[5.5rem] overflow-hidden">
                      {followedTeams.map((teamName) => (
                        <button
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/[0.1] text-white/50 text-[11px] font-medium cursor-pointer hover:bg-white/[0.1] hover:border-white/20 hover:text-white/70 transition-all duration-200"
                          key={teamName}
                          onClick={() =>
                            navigate(
                              `/web-player/sports/team/${encodeURIComponent(teamName)}`
                            )
                          }
                        >
                          <i className="fas fa-heart text-blue-400/60 text-[8px]" />
                          {teamName}
                        </button>
                      ))}
                    </div>
                    <button
                      className="mt-3 px-4 py-2 rounded-lg bg-white/[0.06] border border-white/[0.1] text-xs text-white/50 hover:bg-white/[0.1] hover:text-white/70 hover:border-white/20 font-medium transition-all"
                      onClick={() => navigate("/web-player/sports/teams")}
                    >
                      View More Teams{" "}
                      <i className="fas fa-arrow-right text-[9px] ml-1" />
                    </button>
                  </div>
                ) : (
                  /* ---- WELCOME HERO (New User) ---- */
                  <div className="flex-1 lg:max-w-[60%] hero-fade-in">
                    {/* LIVE Badge - clickable to scroll to Live Now strip */}
                    {liveCount > 0 && (
                      <button
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/15 border border-red-500/30 mb-6 hero-fade-in-delay-1 cursor-pointer hover:bg-red-500/25 hover:border-red-500/50 transition-all duration-200"
                        onClick={() => {
                          const el = document.querySelector("#live-now-strip");

                          if (el)
                            el.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                        }}
                      >
                        <span className="w-2 h-2 rounded-full bg-red-500 live-pulse" />
                        <span className="text-red-400 text-xs font-bold uppercase tracking-wider">
                          Live &middot; {liveCount} Game
                          {liveCount !== 1 ? "s" : ""}
                        </span>
                        <i className="fas fa-chevron-down text-red-400/60 text-[10px] ml-1" />
                      </button>
                    )}

                    {/* Title */}
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 leading-tight tracking-tight hero-fade-in-delay-2">
                      WELCOME, {(profile?.name || "Sports Fan").toUpperCase()}
                      <br />
                      <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
                        LUNOVA SPORTS
                      </span>
                    </h1>

                    {/* Subtitle */}
                    <p className="text-lg md:text-xl text-white/60 mb-8 max-w-lg hero-fade-in-delay-3">
                      Follow your teams. Never miss a moment.
                    </p>

                    {/* CTA Buttons */}
                    <div className="flex flex-wrap gap-4 hero-fade-in-delay-4">
                      <button
                        className={`px-8 py-3.5 rounded-xl font-semibold text-base transition-all duration-200 ${
                          focusedGroup === "hero" && focusedHeroBtn === 0
                            ? "bg-blue-500 text-white shadow-lg shadow-blue-500/30 scale-105 ring-2 ring-blue-400/50"
                            : "bg-blue-600 text-white hover:bg-blue-500 hover:shadow-lg hover:shadow-blue-500/25"
                        }`}
                        onClick={() => setShowTeamPicker(true)}
                        onFocus={() => {
                          setFocusedGroup("hero");
                          setFocusedHeroBtn(0);
                        }}
                      >
                        <i className="fas fa-heart mr-2" />
                        Pick Your Teams
                      </button>
                      <button
                        className={`px-8 py-3.5 rounded-xl font-semibold text-base transition-all duration-200 ${
                          focusedGroup === "hero" && focusedHeroBtn === 1
                            ? "bg-white/15 text-white border border-white/40 shadow-lg shadow-white/10 scale-105 ring-2 ring-white/30"
                            : "bg-white/5 text-white/80 border border-white/20 hover:bg-white/10 hover:text-white hover:border-white/30"
                        }`}
                        onClick={() => navigate("/web-player/sports/leagues")}
                        onFocus={() => {
                          setFocusedGroup("hero");
                          setFocusedHeroBtn(1);
                        }}
                      >
                        <i className="fas fa-trophy mr-2" />
                        Browse All Leagues
                      </button>
                    </div>
                  </div>
                )}

                {/* Right Column - Live Game Mini Cards (40%) */}
                <div className="w-full lg:w-[40%] lg:max-w-[480px] hero-fade-in-delay-3">
                  <h3 className="text-xs font-bold text-white/30 uppercase tracking-widest mb-3">
                    My Teams
                  </h3>

                  {/* No followed teams — just show Add Teams */}
                  {followedTeams.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 px-6 rounded-2xl bg-white/[0.04] border border-dashed border-white/10">
                      <div className="w-14 h-14 rounded-full bg-white/[0.06] flex items-center justify-center mb-4">
                        <i className="fas fa-heart text-white/20 text-xl" />
                      </div>
                      <p className="text-sm text-white/40 text-center mb-4">
                        Follow teams to see their games here
                      </p>
                      <button
                        className="px-6 py-2.5 rounded-xl bg-blue-600/40 border border-blue-500/40 text-blue-300 text-sm font-semibold hover:bg-blue-600/50 hover:text-blue-200 focus:bg-blue-600/50 focus:text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400/50 outline-none transition-all duration-200 flex items-center gap-2"
                        tabIndex={0}
                        onClick={() => setShowTeamPicker(true)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") setShowTeamPicker(true);
                        }}
                      >
                        <i className="fas fa-plus text-[10px]" />
                        Add Teams
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3">
                        {/* Show live games first, then upcoming/final for followed teams */}
                        {(() => {
                          const SIDEBAR_MAX = 4;
                          // Live cards must come from followed-team statuses, not all live games
                          const seenIds = new Set();
                          const liveCards = followedTeamStatuses
                            .filter((ts) => ts.status === "live" && ts.game)
                            .map((ts) => ts.game)
                            .filter((g) => {
                              const id = g.id || `${g.homeTeam}-${g.awayTeam}`;

                              if (seenIds.has(id)) return false;
                              seenIds.add(id);

                              return true;
                            })
                            .slice(0, SIDEBAR_MAX);
                          const nonLiveStatuses = followedTeamStatuses.filter(
                            (ts) => ts.status !== "live"
                          );
                          const maxNonLive = SIDEBAR_MAX - liveCards.length;
                          const shownNonLive = nonLiveStatuses.slice(
                            0,
                            Math.max(0, maxNonLive)
                          );

                          return (
                            <>
                              {/* Live game cards */}
                              {liveCards.map((game, index) => {
                                const isSidebarFocused =
                                  focusedGroup === "sidebar" &&
                                  focusedSidebarCard === index;
                                const sidebarGroupActive =
                                  focusedGroup === "sidebar";

                                return (
                                  <div
                                    className={`relative bg-white/[0.10] backdrop-blur-md rounded-xl border transition-all duration-200 cursor-pointer group ${
                                      isSidebarFocused
                                        ? "border-blue-500/50 bg-white/[0.18] scale-[1.02] shadow-lg shadow-blue-500/15"
                                        : sidebarGroupActive &&
                                            !isSidebarFocused
                                          ? "border-white/[0.08] opacity-60"
                                          : "border-white/[0.15] hover:border-white/25 hover:bg-white/[0.15] hover:scale-105 focus:scale-105"
                                    }`}
                                    key={game.id || index}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                      if (longPressDidFire.current) return;
                                      navigateToEvent(game);
                                    }}
                                    onContextMenu={(e) =>
                                      handleGameContextMenu(e, game)
                                    }
                                    onKeyDown={(e) =>
                                      handleCardKeyDown(e, game)
                                    }
                                    onKeyUp={handleCardKeyUp}
                                  >
                                    <div className="p-3 flex items-center gap-3">
                                      <div className="flex flex-col items-center gap-0.5 w-12 flex-shrink-0">
                                        <span className="text-[10px] font-bold text-white/40 uppercase tracking-wider">
                                          {game.league || game.genre}
                                        </span>
                                        <span className="text-[10px] text-white/30">
                                          {game.period || ""}
                                        </span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                          <div className="flex items-center gap-2 min-w-0 flex-1">
                                            {game.awayTeamLogo && (
                                              <img
                                                alt=""
                                                className="w-5 h-5 object-contain flex-shrink-0"
                                                src={game.awayTeamLogo}
                                                onError={(e) => {
                                                  e.target.style.display =
                                                    "none";
                                                }}
                                              />
                                            )}
                                            <span className="text-sm text-white/90 font-medium truncate">
                                              {game.awayTeam}
                                            </span>
                                            {followedTeams.includes(
                                              game.awayTeam
                                            ) && (
                                              <i className="fas fa-heart text-blue-400/50 text-[8px] flex-shrink-0" />
                                            )}
                                          </div>
                                          <span className="text-sm font-bold text-white tabular-nums">
                                            {spoilerFree
                                              ? "--"
                                              : (game.awayTeamScore ?? 0)}
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between gap-2">
                                          <div className="flex items-center gap-2 min-w-0 flex-1">
                                            {game.homeTeamLogo && (
                                              <img
                                                alt=""
                                                className="w-5 h-5 object-contain flex-shrink-0"
                                                src={game.homeTeamLogo}
                                                onError={(e) => {
                                                  e.target.style.display =
                                                    "none";
                                                }}
                                              />
                                            )}
                                            <span className="text-sm text-white/90 font-medium truncate">
                                              {game.homeTeam}
                                            </span>
                                            {followedTeams.includes(
                                              game.homeTeam
                                            ) && (
                                              <i className="fas fa-heart text-blue-400/50 text-[8px] flex-shrink-0" />
                                            )}
                                          </div>
                                          <span className="text-sm font-bold text-white tabular-nums">
                                            {spoilerFree
                                              ? "--"
                                              : (game.homeTeamScore ?? 0)}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-500/15 border border-red-500/30 flex-shrink-0">
                                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 live-pulse" />
                                        <span className="text-[10px] font-bold text-red-400 uppercase">
                                          Live
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}

                              {/* Non-live team status cards (upcoming / final) */}
                              {shownNonLive.map((ts) => {
                                const teamBadge =
                                  ts.color?.badge || ts.teamInfo?.logo || null;

                                return (
                                  <div
                                    className="relative bg-white/[0.10] backdrop-blur-md rounded-xl border border-white/[0.15] transition-all duration-200 cursor-pointer hover:border-white/25 hover:bg-white/[0.15] hover:scale-105 focus:scale-105 focus:border-blue-500/50 focus:bg-white/[0.18] outline-none"
                                    key={`sidebar-${ts.teamName}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                      if (ts.game) {
                                        navigate(
                                          `/web-player/sports/event/${ts.game.id || ts.game.mediaId || "live"}`,
                                          {
                                            state: {
                                              game: ts.game,
                                              playlist,
                                              profile,
                                            },
                                          }
                                        );
                                      } else {
                                        navigate(
                                          `/web-player/sports/team/${encodeURIComponent(ts.teamName)}`
                                        );
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        if (ts.game) {
                                          navigate(
                                            `/web-player/sports/event/${ts.game.id || ts.game.mediaId || "live"}`,
                                            {
                                              state: {
                                                game: ts.game,
                                                playlist,
                                                profile,
                                              },
                                            }
                                          );
                                        } else {
                                          navigate(
                                            `/web-player/sports/team/${encodeURIComponent(ts.teamName)}`
                                          );
                                        }
                                      }
                                    }}
                                  >
                                    <div className="p-3 flex items-center gap-3">
                                      {/* Team badge */}
                                      {teamBadge ? (
                                        <img
                                          alt={ts.teamName}
                                          className="w-8 h-8 object-contain flex-shrink-0"
                                          src={teamBadge}
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                          }}
                                        />
                                      ) : (
                                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                                          <i
                                            className={`${SPORT_ICONS[ts.teamInfo?.sport] || "fas fa-trophy"} text-white/30 text-xs`}
                                          />
                                        </div>
                                      )}

                                      {/* Team + opponent info */}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-white truncate">
                                          {ts.teamName}
                                        </p>
                                        {ts.status === "upcoming" && (
                                          <p className="text-xs text-white/40 truncate">
                                            vs {ts.opponent}{" "}
                                            {ts.startTime
                                              ? `· ${ts.startTime}`
                                              : "· Today"}
                                          </p>
                                        )}
                                        {ts.status === "final" && (
                                          <p className="text-xs text-white/40 truncate">
                                            {spoilerFree
                                              ? `vs ${ts.opponent}`
                                              : `${ts.teamScore ?? 0} - ${ts.opponentScore ?? 0} vs ${ts.opponent}`}
                                          </p>
                                        )}
                                        {ts.status === "idle" && (
                                          <p className="text-xs text-white/30 truncate">
                                            {ts.league || "No game today"}
                                          </p>
                                        )}
                                      </div>

                                      {/* Status pill */}
                                      {ts.status === "upcoming" && (
                                        <div className="px-2 py-1 rounded-md bg-blue-500/10 border border-blue-500/20 flex-shrink-0">
                                          <span className="text-[10px] font-bold text-blue-400 uppercase">
                                            Upcoming
                                          </span>
                                        </div>
                                      )}
                                      {ts.status === "final" && (
                                        <div className="px-2 py-1 rounded-md bg-white/5 border border-white/10 flex-shrink-0">
                                          <span className="text-[10px] font-bold text-white/40 uppercase">
                                            Final
                                          </span>
                                        </div>
                                      )}
                                      {ts.status === "idle" && (
                                        <div className="px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] flex-shrink-0">
                                          <span className="text-[10px] font-bold text-white/25 uppercase">
                                            Off
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          );
                        })()}
                      </div>

                      {/* Add Teams + View All Teams buttons */}
                      <button
                        className="w-full mt-3 py-2.5 rounded-xl bg-blue-600/30 border border-blue-500/35 text-blue-300 text-sm font-medium hover:bg-blue-600/40 hover:text-blue-200 hover:border-blue-500/50 focus:bg-blue-600/40 focus:text-blue-200 focus:border-blue-500/50 focus:outline-none outline-none transition-all duration-200 flex items-center justify-center gap-2"
                        tabIndex={0}
                        onClick={() => setShowTeamPicker(true)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") setShowTeamPicker(true);
                        }}
                      >
                        <i className="fas fa-plus text-[10px]" />
                        Add Teams
                      </button>
                      <button
                        className="w-full mt-2 py-2.5 rounded-xl bg-white/[0.10] border border-white/[0.15] text-white/60 text-sm font-medium hover:bg-white/[0.15] hover:text-white/80 hover:border-white/25 focus:bg-white/[0.15] focus:text-white/80 focus:border-white/25 focus:outline-none outline-none transition-all duration-200 flex items-center justify-center gap-2"
                        tabIndex={0}
                        onClick={() => navigate("/web-player/sports/teams")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter")
                            navigate("/web-player/sports/teams");
                        }}
                      >
                        View All Teams
                        <i className="fas fa-chevron-right text-[10px]" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Hero bottom gradient — fades into app_background */}
          <div
            className="pointer-events-none"
            style={{
              height: "10rem",
              background:
                "linear-gradient(rgba(10,14,26,1) 0%, transparent 100%)",
            }}
          />

          {/* ============================================ */}
          {/* SPORT CATEGORY PILLS                         */}
          {/* ============================================ */}
          {leagues.length > 0 && (
            <div
              className="relative z-10 px-8 md:px-16 lg:px-24"
              style={{ marginTop: "-140px" }}
            >
              <div
                className="edge-row overflow-x-clip overflow-y-visible"
                ref={sportPillsRef}
                style={{ touchAction: "pan-y", overscrollBehavior: "contain" }}
              >
                <div className="flex gap-2">
                  {leagues.map((sport) => (
                    <button
                      className="flex-shrink-0 flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap bg-white/[0.06] text-white/40 border border-white/[0.08] hover:bg-white/[0.12] hover:text-white/70 hover:border-white/20"
                      key={sport.id}
                      onClick={() =>
                        navigate(`/web-player/sports/hub/${sport.id}`)
                      }
                    >
                      <i className={`fas ${sport.icon} text-[10px]`} />
                      {sport.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ============================================ */}
          {/* SECTION 2: LIVE NOW HORIZONTAL STRIP         */}
          {/* ============================================ */}
          <div
            className="relative z-10 px-8 md:px-16 lg:px-24 pb-8 mt-10"
            id="live-now-strip"
          >
            {/* Section Header */}
            <div className="flex items-center gap-3 mb-4">
              <span className="w-2 h-2 rounded-full bg-red-500 live-pulse" />
              <h2 className="text-xl font-bold text-white">Live Now</h2>
              <span className="text-sm text-white/40">
                {liveNowGames.length} games
              </span>
              <div className="flex-1" />
              <button
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                  heartbeatMode
                    ? "bg-green-500/20 border border-green-500/40 text-green-300"
                    : "bg-white/[0.06] border border-white/[0.1] text-white/40 hover:bg-white/[0.1] hover:text-white/60"
                }`}
                title={
                  heartbeatMode
                    ? "Switch to normal view"
                    : "Show live heartbeat"
                }
                onClick={() => setHeartbeatMode((prev) => !prev)}
              >
                <i
                  className={`fas ${heartbeatMode ? "fa-heartbeat" : "fa-chart-line"} text-[10px]`}
                />
                {heartbeatMode ? "Heartbeat" : "Pulse"}
              </button>
            </div>

            {/* Horizontal Scrolling Cards - Embla Carousel */}
            <div
              className="edge-row overflow-x-clip overflow-y-visible"
              ref={liveNowEmblaRef}
              style={{ touchAction: "pan-y", overscrollBehavior: "contain" }}
            >
              <div className="flex gap-4" ref={liveNowScrollRef}>
                {liveNowGames.map((game, index) => {
                  const isFocused =
                    focusedGroup === "livenow" && focusedLiveCard === index;
                  const isLive = game.rating === "LIVE";
                  const gameId = game.id || `${game.homeTeam}-${game.awayTeam}`;
                  const isPulsing = heartbeatPulse[gameId];
                  const isFollowedHome = followedTeams.includes(game.homeTeam);
                  const isFollowedAway = followedTeams.includes(game.awayTeam);
                  const pulseColor =
                    isPulsing === "home" && isFollowedHome
                      ? "ring-green-500/40"
                      : isPulsing === "away" && isFollowedAway
                        ? "ring-green-500/40"
                        : isPulsing
                          ? "ring-red-500/30"
                          : "";

                  return (
                    <div
                      data-live-card
                      className={`relative flex-shrink-0 w-[220px] snap-start bg-white/[0.04] backdrop-blur-md rounded-xl border shadow-md shadow-black/25 transition-all duration-200 cursor-pointer ${
                        isFocused
                          ? "border-blue-500/50 bg-white/[0.08] scale-105 shadow-lg shadow-blue-500/15"
                          : "border-white/[0.08] hover:border-white/20 hover:bg-white/[0.07] hover:scale-105 focus:scale-105"
                      } ${isPulsing ? `heartbeat-pulse ring-2 ${pulseColor}` : ""}`}
                      key={`live-${game.id || index}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (longPressDidFire.current) return;
                        navigateToEvent(game);
                      }}
                      onContextMenu={(e) => handleGameContextMenu(e, game)}
                      onFocus={() => {
                        setFocusedGroup("livenow");
                        setFocusedLiveCard(index);
                      }}
                      onKeyDown={(e) => handleCardKeyDown(e, game)}
                      onKeyUp={handleCardKeyUp}
                    >
                      <div className="p-4">
                        {/* League + Status */}
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[10px] font-bold text-white/40 uppercase tracking-wider">
                            {game.league || game.genre}
                          </span>
                          {isLive ? (
                            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/30">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500 live-pulse" />
                              <span className="text-[9px] font-bold text-red-400 uppercase">
                                Live
                              </span>
                            </div>
                          ) : (
                            <span className="text-[10px] text-white/30">
                              {game.rating}
                            </span>
                          )}
                        </div>

                        {/* Away Team */}
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {game.awayTeamLogo && (
                              <img
                                alt=""
                                className="w-5 h-5 object-contain flex-shrink-0"
                                src={game.awayTeamLogo}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            )}
                            <span className="text-sm text-white/80 truncate">
                              {game.awayTeam}
                            </span>
                            {isFollowedAway && (
                              <i className="fas fa-heart text-blue-400/50 text-[8px] flex-shrink-0" />
                            )}
                          </div>
                          <span className="text-base font-bold text-white tabular-nums">
                            {spoilerFree ? "--" : (game.awayTeamScore ?? 0)}
                          </span>
                        </div>

                        {/* Home Team */}
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {game.homeTeamLogo && (
                              <img
                                alt=""
                                className="w-5 h-5 object-contain flex-shrink-0"
                                src={game.homeTeamLogo}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            )}
                            <span className="text-sm text-white/80 truncate">
                              {game.homeTeam}
                            </span>
                            {isFollowedHome && (
                              <i className="fas fa-heart text-blue-400/50 text-[8px] flex-shrink-0" />
                            )}
                          </div>
                          <span className="text-base font-bold text-white tabular-nums">
                            {spoilerFree ? "--" : (game.homeTeamScore ?? 0)}
                          </span>
                        </div>

                        {/* Period */}
                        {game.period && (
                          <div className="mt-2 pt-2 border-t border-white/[0.06] text-center">
                            <span className="text-[10px] text-white/30">
                              {game.period}
                            </span>
                          </div>
                        )}

                        {/* Heartbeat sparklines */}
                        {heartbeatMode && heartbeatData[gameId] && (
                          <div className="mt-2 pt-2 border-t border-white/[0.06] flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1">
                              <span className="text-[9px] text-white/20">
                                {game.awayTeam
                                  ?.split(" ")
                                  .pop()
                                  ?.slice(0, 3)
                                  .toUpperCase()}
                              </span>
                              {renderSparkline(
                                heartbeatData[gameId],
                                "away",
                                isFollowedAway
                                  ? "#22c55e"
                                  : "rgba(255,255,255,0.3)"
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {renderSparkline(
                                heartbeatData[gameId],
                                "home",
                                isFollowedHome
                                  ? "#22c55e"
                                  : "rgba(255,255,255,0.3)"
                              )}
                              <span className="text-[9px] text-white/20">
                                {game.homeTeam
                                  ?.split(" ")
                                  .pop()
                                  ?.slice(0, 3)
                                  .toUpperCase()}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="border-t border-white/5 mx-6 my-2" />

          {/* ============================================ */}
          {/* MY TEAMS — Dynamic Status Cards               */}
          {/* ============================================ */}
          <div className="relative z-10 px-8 md:px-16 lg:px-24 py-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <i className="fas fa-heart text-red-400/60 text-lg" />
                <h2 className="text-2xl font-bold text-white">My Teams</h2>
                {followedTeams.length > 0 && (
                  <span className="text-sm font-semibold text-white/40 bg-white/10 px-2.5 py-0.5 rounded-full">
                    {followedTeams.length}
                  </span>
                )}
              </div>
              {followedTeams.length > 0 && (
                <button
                  className="text-sm font-semibold text-blue-400 focus:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50 rounded-lg px-3 py-1.5 transition-colors"
                  tabIndex={0}
                  onClick={() => navigate("/web-player/sports/teams")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") navigate("/web-player/sports/teams");
                  }}
                >
                  View All <i className="fas fa-chevron-right text-xs ml-1" />
                </button>
              )}
            </div>

            {/* Empty state — no followed teams */}
            {followedTeams.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 px-8 rounded-2xl bg-white/[0.03] border-2 border-dashed border-white/10">
                <div className="w-20 h-20 rounded-full bg-white/[0.06] flex items-center justify-center mb-5">
                  <i className="fas fa-users text-white/20 text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-white/70 mb-2">
                  No teams yet
                </h3>
                <p className="text-base text-white/40 text-center max-w-md mb-6">
                  Follow your favorite teams to see live scores, upcoming
                  fixtures, and results right here.
                </p>
                <button
                  className="px-8 py-3 rounded-xl bg-blue-600 text-white font-bold text-lg shadow-lg shadow-blue-500/20 focus:bg-blue-500 focus:scale-[1.04] focus:shadow-xl focus:shadow-blue-500/30 focus:outline-none focus:ring-2 focus:ring-blue-400/60 outline-none transition-all duration-200"
                  tabIndex={0}
                  onClick={() => setShowTeamPicker(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setShowTeamPicker(true);
                  }}
                >
                  <i className="fas fa-plus mr-2" />
                  Follow Teams
                </button>
              </div>
            )}

            {/* Team status cards — horizontal Embla rail */}
            {followedTeamStatuses.length > 0 && (
              <div
                className="edge-row overflow-x-clip overflow-y-visible"
                ref={myTeamsEmblaRef}
                style={{ touchAction: "pan-y", overscrollBehavior: "contain" }}
              >
                <div className="flex gap-4">
                  {followedTeamStatuses.map((ts) => {
                    const teamBadge =
                      ts.color?.badge || ts.teamInfo?.logo || null;
                    const primaryHex = ts.color?.primary || "3b82f6";
                    const r = parseInt(primaryHex.slice(0, 2), 16);
                    const g = parseInt(primaryHex.slice(2, 4), 16);
                    const b = parseInt(primaryHex.slice(4, 6), 16);

                    return (
                      <div
                        className="flex-shrink-0 w-72 rounded-2xl border-2 border-white/[0.06] shadow-lg shadow-black/30 hover:scale-105 focus:border-blue-400/60 focus:scale-[1.05] focus:shadow-xl focus:shadow-blue-500/15 outline-none transition-all duration-200 cursor-pointer relative overflow-hidden"
                        key={ts.teamName}
                        role="button"
                        style={{
                          background: `linear-gradient(135deg, rgba(${r},${g},${b},0.12) 0%, rgba(${r},${g},${b},0.04) 50%, rgba(255,255,255,0.02) 100%)`,
                          borderTop: `3px solid rgba(${r},${g},${b},0.6)`,
                        }}
                        tabIndex={0}
                        onClick={() => {
                          if (ts.game) {
                            navigate(
                              `/web-player/sports/event/${ts.game.id || ts.game.mediaId || "live"}`,
                              {
                                state: {
                                  game: ts.game,
                                  playlist,
                                  profile,
                                },
                              }
                            );
                          } else {
                            navigate(
                              `/web-player/sports/team/${encodeURIComponent(ts.teamName)}`
                            );
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            if (ts.game) {
                              navigate(
                                `/web-player/sports/event/${ts.game.id || ts.game.mediaId || "live"}`,
                                {
                                  state: {
                                    game: ts.game,
                                    playlist,
                                    profile,
                                  },
                                }
                              );
                            } else {
                              navigate(
                                `/web-player/sports/team/${encodeURIComponent(ts.teamName)}`
                              );
                            }
                          }
                        }}
                      >
                        <div className="p-5">
                          {/* Top row: team badge + name + status badge */}
                          <div className="flex items-center gap-3 mb-4">
                            {teamBadge ? (
                              <img
                                alt={ts.teamName}
                                className="w-12 h-12 object-contain flex-shrink-0"
                                src={teamBadge}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                                <i
                                  className={`${SPORT_ICONS[ts.teamInfo?.sport] || "fas fa-trophy"} text-white/40 text-lg`}
                                />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base font-bold text-white truncate">
                                {ts.teamName}
                              </h3>
                              <p className="text-xs text-white/40 truncate">
                                {ts.league || ""}
                              </p>
                            </div>
                            {/* Status badge */}
                            {ts.status === "live" && (
                              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/30 flex-shrink-0">
                                <span className="w-2 h-2 rounded-full bg-red-500 live-pulse" />
                                <span className="text-[11px] font-bold text-red-400 uppercase">
                                  Live
                                </span>
                              </div>
                            )}
                            {ts.status === "upcoming" && (
                              <div className="px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 flex-shrink-0">
                                <span className="text-[11px] font-bold text-blue-400 uppercase">
                                  Soon
                                </span>
                              </div>
                            )}
                            {ts.status === "final" && (
                              <div className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 flex-shrink-0">
                                <span className="text-[11px] font-bold text-white/40 uppercase">
                                  Final
                                </span>
                              </div>
                            )}
                          </div>

                          {/* LIVE: show score matchup */}
                          {ts.status === "live" && (
                            <div className="flex items-center justify-between bg-black/20 rounded-xl px-4 py-3">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span
                                  className="text-3xl font-black text-white tabular-nums"
                                  style={{
                                    color: `rgba(${r},${g},${b},0.9)`,
                                  }}
                                >
                                  {spoilerFree ? "--" : (ts.teamScore ?? 0)}
                                </span>
                              </div>
                              <div className="flex flex-col items-center mx-3">
                                <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">
                                  {ts.period || "LIVE"}
                                </span>
                                <span className="text-lg text-white/20 font-light">
                                  vs
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                                <span className="text-3xl font-black text-white/60 tabular-nums">
                                  {spoilerFree ? "--" : (ts.opponentScore ?? 0)}
                                </span>
                                {ts.opponentLogo && (
                                  <img
                                    alt={ts.opponent}
                                    className="w-8 h-8 object-contain flex-shrink-0"
                                    src={ts.opponentLogo}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          )}

                          {/* UPCOMING: show opponent + time */}
                          {ts.status === "upcoming" && (
                            <div className="flex items-center gap-3 bg-black/15 rounded-xl px-4 py-3">
                              {ts.opponentLogo && (
                                <img
                                  alt={ts.opponent}
                                  className="w-8 h-8 object-contain flex-shrink-0"
                                  src={ts.opponentLogo}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-white truncate">
                                  vs {ts.opponent}
                                </p>
                                <p className="text-xs text-white/40 mt-0.5">
                                  {ts.startTime || "Today"}
                                </p>
                              </div>
                              <i className="fas fa-clock text-blue-400/50 text-sm" />
                            </div>
                          )}

                          {/* FINAL: show result */}
                          {ts.status === "final" && (
                            <div className="flex items-center justify-between bg-black/15 rounded-xl px-4 py-3">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-2xl font-black text-white/70 tabular-nums">
                                  {spoilerFree ? "--" : (ts.teamScore ?? 0)}
                                </span>
                              </div>
                              <div className="flex flex-col items-center mx-3">
                                <span className="text-[10px] font-bold text-white/30 uppercase tracking-wider">
                                  Final
                                </span>
                                <span className="text-lg text-white/15 font-light">
                                  vs
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                                <span className="text-2xl font-black text-white/40 tabular-nums">
                                  {spoilerFree ? "--" : (ts.opponentScore ?? 0)}
                                </span>
                                {ts.opponentLogo && (
                                  <img
                                    alt={ts.opponent}
                                    className="w-8 h-8 object-contain flex-shrink-0 opacity-50"
                                    src={ts.opponentLogo}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          )}

                          {/* IDLE: not playing */}
                          {ts.status === "idle" && (
                            <div className="flex items-center gap-3 bg-black/10 rounded-xl px-4 py-3">
                              <i className="fas fa-moon text-white/15 text-sm" />
                              <p className="text-sm text-white/30">
                                Not playing today
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* View All card */}
                  <div
                    className="flex-shrink-0 w-44 rounded-2xl border-2 border-dashed border-white/10 bg-white/[0.02] shadow-md shadow-black/20 flex flex-col items-center justify-center cursor-pointer hover:scale-105 focus:border-blue-400/40 focus:bg-white/[0.04] focus:scale-105 outline-none transition-all duration-200"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate("/web-player/sports/teams")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        navigate("/web-player/sports/teams");
                    }}
                  >
                    <div className="flex flex-col items-center gap-3 p-6">
                      <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <i className="fas fa-th-large text-blue-400/60 text-lg" />
                      </div>
                      <span className="text-sm font-semibold text-blue-400">
                        View All
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ============================================ */}
          {/* MY PLAYERS ROW                               */}
          {/* ============================================ */}
          {followedPlayersList.length > 0 && (
            <div className="relative z-10 px-8 md:px-16 lg:px-24 py-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <i className="fas fa-user-friends text-purple-400/60 text-lg" />
                  <h2 className="text-2xl font-bold text-white">My Players</h2>
                  <span className="bg-purple-500/20 text-purple-300 text-xs font-bold px-2.5 py-1 rounded-full">
                    {followedPlayersList.length}
                  </span>
                </div>
              </div>
              <div
                className="edge-row overflow-x-clip overflow-y-visible"
                ref={myPlayersEmblaRef}
                style={{ touchAction: "pan-y", overscrollBehavior: "contain" }}
              >
                <div className="flex gap-5">
                  {followedPlayersList.map((player) => {
                    const meta = player.metadata || {};
                    const ringColor =
                      SPORT_RING_COLORS[meta.sport] ||
                      "from-purple-500 to-purple-700";

                    return (
                      <div
                        className="flex-shrink-0 w-44 bg-white/[0.04] backdrop-blur-md rounded-xl border-2 border-white/[0.08] shadow-md shadow-black/25 hover:scale-105 focus:border-purple-500/60 focus:bg-white/[0.08] focus:scale-105 focus:shadow-lg focus:shadow-purple-500/10 outline-none transition-all duration-200 cursor-pointer p-5 flex flex-col items-center text-center"
                        key={player.key}
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          navigate(
                            `/web-player/sports/player/${meta.id || player.key}`
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter")
                            navigate(
                              `/web-player/sports/player/${meta.id || player.key}`
                            );
                        }}
                      >
                        {/* Avatar with sport-colored ring */}
                        <div
                          className={`p-[3px] rounded-full bg-gradient-to-br ${ringColor} mb-3 shadow-lg shadow-black/30`}
                        >
                          <div className="rounded-full overflow-hidden bg-gray-900 w-20 h-20">
                            {meta.photo ? (
                              <img
                                alt={meta.name}
                                className="w-full h-full object-cover object-top"
                                src={meta.photo}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <i className="fas fa-user text-white/20 text-2xl" />
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Name */}
                        <h3 className="text-base font-bold text-white leading-tight mb-1 truncate w-full">
                          {meta.name || player.key}
                        </h3>
                        {/* Position + Team */}
                        {meta.position && (
                          <p className="text-sm text-purple-300/60 mb-0.5">
                            {meta.position}
                          </p>
                        )}
                        {meta.teamName && (
                          <p className="text-xs text-white/30 truncate w-full">
                            {meta.teamName}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ============================================ */}
          {/* AI GAME RECAPS - Recently finished games     */}
          {/* ============================================ */}
          {gameRecaps.length > 0 && (
            <div className="relative z-10 px-8 md:px-16 lg:px-24 py-6">
              <div className="flex items-center gap-3 mb-4">
                <i className="fas fa-robot text-amber-400/60 text-sm" />
                <h2 className="text-lg font-bold text-white">Game Recaps</h2>
                <span className="text-[10px] font-bold text-amber-400/60 uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20">
                  AI Generated
                </span>
              </div>
              <div className="edge-row flex gap-4 overflow-x-auto pb-2 scrollbar-thin">
                {gameRecaps.map((recap) => (
                  <div
                    className="flex-shrink-0 w-[320px] bg-white/[0.03] backdrop-blur-md rounded-xl border border-amber-500/15 shadow-md shadow-black/25 hover:border-amber-500/30 transition-all duration-200 cursor-pointer group"
                    key={recap.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => recap.game && navigateToEvent(recap.game)}
                    onKeyDown={activateOnEnterOrSpace}
                  >
                    <div className="p-4">
                      {/* League badge */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[10px] font-bold text-white/40 uppercase tracking-wider">
                          {recap.league}
                        </span>
                        <span className="text-[10px] text-amber-400/50 ml-auto">
                          Final
                        </span>
                      </div>

                      {/* Teams + Score */}
                      <div className="flex items-center gap-3 mb-3">
                        {recap.awayLogo && (
                          <img
                            alt=""
                            className="w-8 h-8 object-contain"
                            src={recap.awayLogo}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <div className="flex items-center gap-1.5 text-sm">
                          <span className="text-white/70 font-medium">
                            {recap.awayTeam}
                          </span>
                          <span className="text-white font-bold tabular-nums">
                            {spoilerFree ? "--" : recap.awayScore}
                          </span>
                          <span className="text-white/30 mx-1">-</span>
                          <span className="text-white font-bold tabular-nums">
                            {spoilerFree ? "--" : recap.homeScore}
                          </span>
                          <span className="text-white/70 font-medium">
                            {recap.homeTeam}
                          </span>
                        </div>
                        {recap.homeLogo && (
                          <img
                            alt=""
                            className="w-8 h-8 object-contain"
                            src={recap.homeLogo}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                      </div>

                      {/* Recap text */}
                      <div className="border-t border-white/[0.06] pt-3">
                        <p className="text-sm text-white/80 font-medium leading-relaxed">
                          {spoilerFree
                            ? "Score hidden — disable spoiler-free mode to see recap"
                            : recap.headline}
                        </p>
                        {!spoilerFree && (
                          <p className="text-xs text-white/40 mt-1">
                            {recap.detail}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Gold accent bottom bar */}
                    <div className="h-0.5 bg-gradient-to-r from-transparent via-amber-500/40 to-transparent rounded-b-xl" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ============================================ */}
          {/* BECAUSE YOU FOLLOW RAILS                     */}
          {/* ============================================ */}
          {becauseYouFollowRails.length > 0 && (
            <div className="px-6 md:px-24 mb-8 space-y-6">
              {becauseYouFollowRails.map((rail) => (
                <div key={rail.entityName}>
                  <div className="flex items-center gap-2 mb-3">
                    {rail.logo && (
                      <img
                        alt=""
                        className="w-5 h-5 object-contain"
                        src={rail.logo}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    <h3 className="text-sm font-bold text-white/50">
                      {rail.label}
                    </h3>
                  </div>
                  <div
                    className="edge-row flex gap-4 overflow-x-auto pb-2"
                    style={{
                      scrollbarWidth: "thin",
                      scrollbarColor: "rgba(255,255,255,0.1) transparent",
                    }}
                  >
                    {rail.games.map((game, idx) => {
                      const isLive = game.rating === "LIVE";

                      return (
                        <div
                          className="flex-shrink-0 w-[220px] bg-white/[0.04] backdrop-blur-md rounded-xl border border-white/[0.08] shadow-md shadow-black/25 hover:scale-105 hover:border-blue-500/30 focus:scale-105 focus:border-blue-500/30 outline-none transition-all cursor-pointer"
                          key={`byf-${rail.entityName}-${game.id || idx}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            if (longPressDidFire.current) return;
                            navigateToEvent(game);
                          }}
                          onContextMenu={(e) => handleGameContextMenu(e, game)}
                          onKeyDown={(e) => handleCardKeyDown(e, game)}
                          onKeyUp={handleCardKeyUp}
                        >
                          <div className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-[10px] font-bold text-white/40 uppercase tracking-wider">
                                {game.league || game.genre}
                              </span>
                              {isLive && (
                                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/30">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 live-pulse" />
                                  <span className="text-[9px] font-bold text-red-400 uppercase">
                                    Live
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                {game.awayTeamLogo && (
                                  <img
                                    alt=""
                                    className="w-5 h-5 object-contain"
                                    src={game.awayTeamLogo}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                )}
                                <span className="text-white/70 text-xs font-medium flex-1 truncate">
                                  {game.awayTeam}
                                </span>
                                <span className="text-white font-bold text-xs tabular-nums">
                                  {spoilerFree
                                    ? "--"
                                    : (game.awayTeamScore ?? "")}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                {game.homeTeamLogo && (
                                  <img
                                    alt=""
                                    className="w-5 h-5 object-contain"
                                    src={game.homeTeamLogo}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                )}
                                <span className="text-white/70 text-xs font-medium flex-1 truncate">
                                  {game.homeTeam}
                                </span>
                                <span className="text-white font-bold text-xs tabular-nums">
                                  {spoilerFree
                                    ? "--"
                                    : (game.homeTeamScore ?? "")}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ============================================ */}
          {/* FEATURED LIVE GAME — Cinematic card           */}
          {/* ============================================ */}
          {liveGames.length > 0 &&
            (() => {
              // Pick a followed-team game first, else first live game
              const featuredGame =
                liveGames.find(
                  (g) =>
                    followedTeams.includes(g.homeTeam) ||
                    followedTeams.includes(g.awayTeam)
                ) || liveGames[0];
              const sportKey =
                featuredGame.sport?.toLowerCase() ||
                featuredGame.genre?.toLowerCase() ||
                "default";
              const bgImage =
                SPORT_HERO_BACKGROUNDS[sportKey] ||
                SPORT_HERO_BACKGROUNDS.default;

              return (
                <div className="relative z-10 px-8 md:px-16 lg:px-24 py-8">
                  <div
                    className="relative rounded-3xl overflow-hidden cursor-pointer outline-none border-2 border-white/[0.06] shadow-xl shadow-black/30 hover:scale-[1.02] focus:border-blue-400/60 focus:scale-[1.03] focus:shadow-2xl transition-all duration-300"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      const state = {
                        game: featuredGame,
                        playlist,
                        profile,
                      };

                      navigate(
                        `/web-player/sports/event/${featuredGame.id || featuredGame.mediaId || "live"}`,
                        { state }
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const state = {
                          game: featuredGame,
                          playlist,
                          profile,
                        };

                        navigate(
                          `/web-player/sports/event/${featuredGame.id || featuredGame.mediaId || "live"}`,
                          { state }
                        );
                      }
                    }}
                  >
                    {/* Sport background image */}
                    <div className="absolute inset-0">
                      <img
                        alt=""
                        className="w-full h-full object-cover"
                        src={bgImage}
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/70" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
                    </div>

                    {/* Content */}
                    <div className="relative z-10 flex flex-col h-96">
                      {/* Header — Day title + LIVE badge + league */}
                      <div className="px-10 pt-8 pb-4 flex items-center justify-between">
                        <div>
                          <h2 className="text-3xl font-bold text-white">
                            {new Date().toLocaleDateString("en-US", {
                              weekday: "long",
                            })}{" "}
                            Game
                          </h2>
                          <p className="text-base text-white/50 mt-1">
                            {new Date().toLocaleDateString("en-US", {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            })}
                            {" · "}
                            {featuredGame.league}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          {featuredGame.period && (
                            <span className="text-base text-white/50 bg-white/10 rounded-full px-4 py-1.5 font-medium">
                              {featuredGame.period}
                            </span>
                          )}
                          <div className="flex items-center gap-2 bg-red-600 rounded-full px-5 py-2 shadow-lg shadow-red-500/30">
                            <div className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
                            <span className="text-sm font-bold text-white uppercase tracking-wider">
                              Live
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="mx-10 border-t border-white/10" />

                      {/* Matchup — centered in remaining space */}
                      <div className="flex-1 flex items-center justify-center px-10">
                        <div className="flex items-center gap-14 w-full justify-center">
                          {/* Home team */}
                          <div className="flex items-center gap-5">
                            {featuredGame.homeTeamLogo && (
                              <img
                                alt={featuredGame.homeTeam}
                                className="w-24 h-24 object-contain drop-shadow-[0_4px_16px_rgba(0,0,0,0.5)]"
                                src={featuredGame.homeTeamLogo}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            )}
                            <span className="text-2xl font-bold text-white max-w-[200px] leading-tight">
                              {featuredGame.homeTeam}
                            </span>
                          </div>

                          {/* Score */}
                          <div className="flex flex-col items-center">
                            <div className="flex items-center gap-6">
                              <span className="text-7xl font-black text-white tabular-nums">
                                {spoilerFree
                                  ? "--"
                                  : (featuredGame.homeTeamScore ?? 0)}
                              </span>
                              <span className="text-4xl font-light text-white/20">
                                -
                              </span>
                              <span className="text-7xl font-black text-white tabular-nums">
                                {spoilerFree
                                  ? "--"
                                  : (featuredGame.awayTeamScore ?? 0)}
                              </span>
                            </div>
                            {featuredGame.venue && (
                              <p className="text-sm text-white/30 mt-2 text-center">
                                {featuredGame.venue}
                              </p>
                            )}
                          </div>

                          {/* Away team */}
                          <div className="flex items-center gap-5">
                            <span className="text-2xl font-bold text-white text-right max-w-[200px] leading-tight">
                              {featuredGame.awayTeam}
                            </span>
                            {featuredGame.awayTeamLogo && (
                              <img
                                alt={featuredGame.awayTeam}
                                className="w-24 h-24 object-contain drop-shadow-[0_4px_16px_rgba(0,0,0,0.5)]"
                                src={featuredGame.awayTeamLogo}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Bottom bar — Watch Now CTA */}
                      <div className="px-10 pb-6 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-white/30">
                          <i className="fas fa-tv" />
                          <span>
                            {featuredGame.league} ·{" "}
                            {featuredGame.season || "2025-26"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-blue-600 focus:bg-blue-500 rounded-xl px-6 py-3 transition-colors shadow-lg shadow-blue-600/30">
                          <i className="fas fa-play text-white text-sm" />
                          <span className="text-base font-bold text-white">
                            Watch Now
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* ============================================ */}
          {/* LEAGUES ROW                                  */}
          {/* ============================================ */}
          <div className="relative z-10 px-8 md:px-16 lg:px-24 py-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <i className="fas fa-trophy text-amber-400/60 text-lg" />
                <h2 className="text-2xl font-bold text-white">Leagues</h2>
                <span className="text-sm font-semibold text-white/40 bg-white/10 px-2.5 py-0.5 rounded-full">
                  {LEAGUES.length}
                </span>
              </div>
              <button
                className="text-sm font-semibold text-blue-400 focus:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50 rounded-lg px-3 py-1.5 transition-colors"
                tabIndex={0}
                onClick={() => navigate("/web-player/sports/leagues")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") navigate("/web-player/sports/leagues");
                }}
              >
                Browse All <i className="fas fa-chevron-right text-xs ml-1" />
              </button>
            </div>
            <div
              className="edge-row overflow-x-clip overflow-y-visible"
              ref={leaguesEmblaRef}
              style={{ touchAction: "pan-y", overscrollBehavior: "contain" }}
            >
              <div className="flex gap-5">
                {LEAGUES.map((league) => {
                  const badgeInfo = leagueBadges[league.id];

                  return (
                    <div
                      className="flex-shrink-0 w-40 bg-white/[0.04] backdrop-blur-md rounded-xl border-2 border-white/[0.08] shadow-md shadow-black/25 hover:scale-105 focus:border-blue-500/60 focus:bg-white/[0.08] focus:scale-105 focus:shadow-lg focus:shadow-blue-500/10 outline-none transition-all duration-200 cursor-pointer p-5 flex flex-col items-center text-center"
                      key={league.id}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        navigate(`/web-player/sports/league/${league.id}`)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          navigate(`/web-player/sports/league/${league.id}`);
                      }}
                    >
                      {badgeInfo?.badge ? (
                        <img
                          alt={badgeInfo.name || league.name}
                          className="w-16 h-16 object-contain mb-3"
                          src={badgeInfo.badge}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-3">
                          <i
                            className={`${SPORT_ICONS[league.sport] || "fas fa-trophy"} text-white/40 text-2xl`}
                          />
                        </div>
                      )}
                      <h3 className="text-base font-semibold text-white truncate w-full">
                        {badgeInfo?.name || league.name}
                      </h3>
                      <p className="text-sm text-white/40 mt-1">
                        {badgeInfo?.country || league.sport}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ============================================ */}
          {/* TEAM HALL OF FAME — 1 row, 2 big cards        */}
          {/* ============================================ */}
          {hofTeamData.length >= 2 && (
            <div className="relative z-10 py-10 px-8 md:px-16 lg:px-24">
              {/* Section header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center shadow-lg shadow-yellow-500/30">
                    <i className="fas fa-trophy text-black text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white">
                      Team Hall of Fame
                    </h2>
                    <p className="text-base text-white/50 mt-0.5">
                      The most decorated clubs in sports history
                    </p>
                  </div>
                </div>
              </div>

              {/* Single row — 2 large cards side by side */}
              <div className="grid grid-cols-2 gap-6">
                {hofTeamData.slice(0, 2).map((team, idx) => {
                  const teamColor = team.color1 || "#3b82f6";
                  const teamColor2 = team.color2 || teamColor;

                  return (
                    <div
                      className="rounded-2xl overflow-hidden relative cursor-pointer outline-none border-2 border-white/[0.06] shadow-lg shadow-black/30 hover:scale-105 focus:border-yellow-400/80 focus:scale-105 focus:shadow-2xl focus:shadow-yellow-500/20 transition-all duration-300"
                      key={team.id}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        navigate(
                          `/web-player/sports/team/${encodeURIComponent(team.name)}`
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          navigate(
                            `/web-player/sports/team/${encodeURIComponent(team.name)}`
                          );
                      }}
                    >
                      {/* Vibrant team color background + fanart */}
                      <div className="absolute inset-0">
                        <div
                          className="absolute inset-0"
                          style={{
                            background: `linear-gradient(135deg, ${teamColor} 0%, ${teamColor2}cc 50%, ${teamColor}80 100%)`,
                          }}
                        />
                        {team.fanart && (
                          <img
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-50"
                            src={team.fanart}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      </div>

                      {/* Content */}
                      <div className="relative z-10 p-8 flex items-center gap-8 h-64">
                        {/* Rank */}
                        <div className="absolute top-4 right-4 bg-black/40 backdrop-blur-sm rounded-full w-10 h-10 flex items-center justify-center">
                          <span className="text-yellow-300 font-black text-base">
                            #{idx + 1}
                          </span>
                        </div>

                        {/* Team badge — large */}
                        <div className="flex-shrink-0">
                          {team.badge ? (
                            <img
                              alt={team.name}
                              className="w-28 h-28 object-contain drop-shadow-[0_6px_20px_rgba(0,0,0,0.6)]"
                              src={team.badge}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-28 h-28 rounded-full bg-white/15 flex items-center justify-center">
                              <i className="fas fa-shield-alt text-white/40 text-5xl" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-3xl font-bold text-white leading-tight mb-2 drop-shadow-lg">
                            {team.name}
                          </h3>
                          <p className="text-base text-white/70 mb-4">
                            {team.league}
                            {team.country ? ` · ${team.country}` : ""}
                            {team.formedYear
                              ? ` · Est. ${team.formedYear}`
                              : ""}
                          </p>
                          {/* Honours pill */}
                          <div className="inline-flex items-center gap-3 bg-yellow-400/20 backdrop-blur-sm border border-yellow-400/30 rounded-full px-5 py-2">
                            <i className="fas fa-trophy text-yellow-300 text-lg" />
                            <span className="text-3xl font-black text-yellow-300">
                              {team.honours}
                            </span>
                            <span className="text-sm text-yellow-200/70 font-semibold uppercase tracking-wider">
                              Honours
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ============================================ */}
          {/* DISCOVER TEAMS ROW                           */}
          {/* ============================================ */}
          {recommendedTeams.length > 0 && (
            <div className="relative z-10 px-8 md:px-16 lg:px-24 py-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <i className="fas fa-compass text-purple-400/60 text-lg" />
                  <h2 className="text-2xl font-bold text-white">
                    Discover Teams
                  </h2>
                </div>
                <button
                  className="text-sm font-semibold text-blue-400 focus:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50 rounded-lg px-3 py-1.5 transition-colors"
                  tabIndex={0}
                  onClick={() => navigate("/web-player/sports/teams")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") navigate("/web-player/sports/teams");
                  }}
                >
                  See All <i className="fas fa-chevron-right text-xs ml-1" />
                </button>
              </div>
              <div
                className="edge-row overflow-x-clip overflow-y-visible"
                ref={discoverTeamsEmblaRef}
                style={{ touchAction: "pan-y", overscrollBehavior: "contain" }}
              >
                <div className="flex gap-5">
                  {recommendedTeams.map((team) => {
                    const color = teamColors[team.name];

                    return (
                      <div
                        className="flex-shrink-0 w-48 bg-white/[0.04] backdrop-blur-md rounded-xl border-2 border-white/[0.08] shadow-md shadow-black/25 hover:scale-105 focus:border-purple-500/60 focus:bg-white/[0.08] focus:scale-105 focus:shadow-lg focus:shadow-purple-500/10 outline-none transition-all duration-200 cursor-pointer relative"
                        key={team.name}
                        role="button"
                        style={{
                          borderTop: color
                            ? `3px solid #${color.primary}`
                            : "3px solid rgba(255,255,255,0.1)",
                        }}
                        tabIndex={0}
                        onClick={() =>
                          navigate(
                            `/web-player/sports/team/${encodeURIComponent(team.name)}`
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter")
                            navigate(
                              `/web-player/sports/team/${encodeURIComponent(team.name)}`
                            );
                        }}
                      >
                        <div className="p-5">
                          <div className="flex items-start justify-between mb-3">
                            {color?.badge ? (
                              <img
                                alt={team.name}
                                className="w-14 h-14 object-contain"
                                src={color.badge}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : team.logo ? (
                              <img
                                alt={team.name}
                                className="w-14 h-14 object-contain"
                                src={team.logo}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
                                <i
                                  className={`${SPORT_ICONS[team.sport] || "fas fa-trophy"} text-white/40 text-xl`}
                                />
                              </div>
                            )}
                            <FollowButton
                              entity={team}
                              size="sm"
                              type="team"
                              variant="icon"
                              onFollowChange={() =>
                                setFollowedTeams(getFavoriteTeams())
                              }
                            />
                          </div>
                          <h3 className="text-base font-semibold text-white truncate">
                            {team.name}
                          </h3>
                          <p className="text-sm text-white/40 truncate mt-1">
                            {team.league || team.sport}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ============================================ */}
          {/* SECTION 3: EXISTING CONTENT ROWS             */}
          {/* ============================================ */}

          {/* Show shimmer loading only if we have no carousel data yet */}
          {loading && carouselItems.length === 0 ? (
            <>
              <style>
                {`
                  @keyframes shimmer {
                    0% { background-position: -1000px 0; }
                    100% { background-position: 1000px 0; }
                  }
                  .shimmer {
                    animation: shimmer 2s infinite linear;
                    background: linear-gradient(to right, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.05) 100%);
                    background-size: 1000px 100%;
                  }
                `}
              </style>
              <div className="px-24 mb-12">
                <div className="shimmer h-8 w-48 mb-6 rounded bg-white/10" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[...Array(4)].map((_, i) => (
                    <div
                      className="shimmer h-48 rounded-lg bg-white/10"
                      key={i}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Featured Games */}
              {carouselItems.length > 0 && (
                <>
                  <FeaturedGames games={carouselItems} />
                  <div className="border-t border-white/5 mx-6 my-2" />
                  <GamesToWatch
                    favoriteTeams={getFavoriteTeams()}
                    games={[...carouselItems, ...(sportsData.upcoming || [])]}
                  />
                  <div className="border-t border-white/5 mx-6 my-2" />
                </>
              )}
            </>
          )}

          {/* ============================================ */}
          {/* HIGHLIGHTS ROW                               */}
          {/* ============================================ */}
          {highlights.length > 0 && (
            <div className="relative z-10 px-8 md:px-16 lg:px-24 py-6">
              <div className="flex items-center gap-3 mb-1">
                <i className="fas fa-play-circle text-blue-400/60 text-lg" />
                <h2 className="text-2xl font-bold text-white">Highlights</h2>
              </div>
              <p className="text-sm text-white/40 mb-5 ml-8">
                Watch highlights from recent matches
              </p>
              <div
                className="edge-row overflow-x-clip overflow-y-visible"
                ref={highlightsEmblaRef}
                style={{ touchAction: "pan-y", overscrollBehavior: "contain" }}
              >
                <div className="flex gap-5">
                  {highlights.map((ev) => (
                    <div
                      className="flex-shrink-0 w-80 cursor-pointer outline-none rounded-xl shadow-md shadow-black/25 hover:scale-105 focus:scale-105 focus:shadow-lg focus:shadow-blue-500/10 transition-all duration-200"
                      key={ev.idEvent}
                      role="link"
                      tabIndex={0}
                      onClick={() => {
                        if (ev.strVideo) window.open(ev.strVideo, "_blank");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && ev.strVideo)
                          window.open(ev.strVideo, "_blank");
                      }}
                    >
                      {/* Thumbnail with play overlay */}
                      <div className="relative rounded-xl overflow-hidden mb-2 aspect-video border-2 border-transparent focus-within:border-blue-500/60">
                        <img
                          alt={ev.strEvent}
                          className="w-full h-full object-cover"
                          src={ev.strThumb}
                          onError={(e) => {
                            e.target.src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 9'%3E%3Crect fill='%23111' width='16' height='9'/%3E%3C/svg%3E";
                          }}
                        />
                        {/* Play button */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-14 h-14 rounded-full bg-white/80 flex items-center justify-center shadow-lg">
                            <i className="fas fa-play text-gray-900 text-xl ml-1" />
                          </div>
                        </div>
                        {/* Score overlay */}
                        {ev.intHomeScore != null && ev.intAwayScore != null && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm px-4 py-2 flex items-center justify-center gap-3">
                            {ev.strHomeTeamBadge && (
                              <img
                                alt=""
                                className="w-6 h-6 object-contain"
                                src={ev.strHomeTeamBadge}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            )}
                            <span className="text-base font-bold text-white">
                              {ev.intHomeScore} - {ev.intAwayScore}
                            </span>
                            {ev.strAwayTeamBadge && (
                              <img
                                alt=""
                                className="w-6 h-6 object-contain"
                                src={ev.strAwayTeamBadge}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                      {/* Event info */}
                      <h3 className="text-base text-white truncate">
                        {ev.strEvent}
                      </h3>
                      <p className="text-sm text-white/40">{ev.dateEvent}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ============================================ */}
          {/* PLAYER HALL OF FAME                          */}
          {/* ============================================ */}
          {hofPlayers.length > 0 && (
            <div className="relative z-10 px-8 md:px-16 lg:px-24 py-6">
              <div className="flex items-center gap-3 mb-5">
                <i className="fas fa-star text-yellow-400/60 text-lg" />
                <h2 className="text-2xl font-bold text-white">Hall of Fame</h2>
                <span className="text-sm text-white/40">
                  Legendary athletes
                </span>
              </div>
              <div
                className="edge-row overflow-x-clip overflow-y-visible"
                ref={hofEmblaRef}
                style={{ touchAction: "pan-y", overscrollBehavior: "contain" }}
              >
                <div className="flex gap-8">
                  {hofPlayers.map((player) => {
                    const ringColor =
                      SPORT_RING_COLORS[player.sport] ||
                      "from-gray-500 to-gray-700";

                    return (
                      <div
                        className="flex-shrink-0 w-36 flex flex-col items-center cursor-pointer outline-none hover:scale-105 focus:scale-[1.08] transition-transform duration-200"
                        key={player.id}
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          navigate(`/web-player/sports/player/${player.id}`)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter")
                            navigate(`/web-player/sports/player/${player.id}`);
                        }}
                      >
                        {/* Gradient ring around circular avatar */}
                        <div
                          className={`p-[3px] rounded-full bg-gradient-to-br ${ringColor} mb-3 shadow-lg shadow-black/30`}
                        >
                          <div className="rounded-full overflow-hidden bg-gray-900 w-28 h-28">
                            {player.cutout ? (
                              <img
                                alt={player.name}
                                className="w-full h-full object-cover object-top"
                                src={player.cutout}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <i className="fas fa-user text-white/20 text-4xl" />
                              </div>
                            )}
                          </div>
                        </div>
                        <h3 className="text-base font-bold text-white text-center leading-tight">
                          {player.name}
                        </h3>
                        <p className="text-sm text-white/40 text-center mt-1">
                          {player.sport}
                        </p>
                        {player.nationality && (
                          <p className="text-xs text-white/30 text-center mt-0.5">
                            {player.nationality}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Premium Overlay */}
      <PremiumOverlay
        feature="sports"
        isOpen={showPremiumOverlay}
        onClose={() => setShowPremiumOverlay(false)}
      />

      {/* ============================================ */}
      {/* RIGHT-CLICK CONTEXT MENU                     */}
      {/* ============================================ */}
      {contextMenu && contextMenu.game && (
        <div
          className="fixed bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl py-2 min-w-[260px]"
          role="presentation"
          style={{
            left: Math.min(contextMenu.x, window.innerWidth - 280),
            top: Math.min(contextMenu.y, window.innerHeight - 380),
            zIndex: 9999,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Follow/Unfollow Home Team */}
          <button
            className="w-full flex items-center gap-3 px-5 py-3 text-sm text-white/80 hover:bg-white/[0.08] transition-colors text-left"
            onClick={() => {
              toggleFavoriteTeam(contextMenu.game.homeTeam);
              setFollowedTeams(getFavoriteTeams());
              dismissContextMenu();
            }}
          >
            <i
              className={`${followedTeams.includes(contextMenu.game.homeTeam) ? "fas text-blue-400" : "far text-white/30"} fa-heart text-xs w-4 text-center`}
            />
            {followedTeams.includes(contextMenu.game.homeTeam)
              ? "Unfollow"
              : "Follow"}{" "}
            {contextMenu.game.homeTeam}
          </button>
          {/* Follow/Unfollow Away Team */}
          <button
            className="w-full flex items-center gap-3 px-5 py-3 text-sm text-white/80 hover:bg-white/[0.08] transition-colors text-left"
            onClick={() => {
              toggleFavoriteTeam(contextMenu.game.awayTeam);
              setFollowedTeams(getFavoriteTeams());
              dismissContextMenu();
            }}
          >
            <i
              className={`${followedTeams.includes(contextMenu.game.awayTeam) ? "fas text-blue-400" : "far text-white/30"} fa-heart text-xs w-4 text-center`}
            />
            {followedTeams.includes(contextMenu.game.awayTeam)
              ? "Unfollow"
              : "Follow"}{" "}
            {contextMenu.game.awayTeam}
          </button>
          {/* Follow Both */}
          {(!followedTeams.includes(contextMenu.game.homeTeam) ||
            !followedTeams.includes(contextMenu.game.awayTeam)) && (
            <button
              className="w-full flex items-center gap-3 px-5 py-3 text-sm text-white/80 hover:bg-white/[0.08] transition-colors text-left"
              onClick={() => {
                if (!followedTeams.includes(contextMenu.game.homeTeam))
                  toggleFavoriteTeam(contextMenu.game.homeTeam);
                if (!followedTeams.includes(contextMenu.game.awayTeam))
                  toggleFavoriteTeam(contextMenu.game.awayTeam);
                setFollowedTeams(getFavoriteTeams());
                dismissContextMenu();
              }}
            >
              <i className="fas fa-heart-circle-plus text-white/30 text-xs w-4 text-center" />
              Follow Both Teams
            </button>
          )}
          {/* Divider */}
          <div className="border-t border-white/[0.08] my-1.5" />
          {/* View Home Team */}
          <button
            className="w-full flex items-center gap-3 px-5 py-3 text-sm text-white/80 hover:bg-white/[0.08] transition-colors text-left"
            onClick={() => {
              navigate(
                `/web-player/sports/team/${encodeURIComponent(contextMenu.game.homeTeam)}`
              );
              dismissContextMenu();
            }}
          >
            <i className="fas fa-shield-alt text-white/30 text-xs w-4 text-center" />
            View {contextMenu.game.homeTeam}
          </button>
          {/* View Away Team */}
          <button
            className="w-full flex items-center gap-3 px-5 py-3 text-sm text-white/80 hover:bg-white/[0.08] transition-colors text-left"
            onClick={() => {
              navigate(
                `/web-player/sports/team/${encodeURIComponent(contextMenu.game.awayTeam)}`
              );
              dismissContextMenu();
            }}
          >
            <i className="fas fa-shield-alt text-white/30 text-xs w-4 text-center" />
            View {contextMenu.game.awayTeam}
          </button>
          {/* Game Details */}
          <button
            className="w-full flex items-center gap-3 px-5 py-3 text-sm text-white/80 hover:bg-white/[0.08] transition-colors text-left"
            onClick={() => {
              navigateToEvent(contextMenu.game);
              dismissContextMenu();
            }}
          >
            <i className="fas fa-info-circle text-white/30 text-xs w-4 text-center" />
            Game Details
          </button>
        </div>
      )}

      {/* ============================================ */}
      {/* PICK YOUR TEAMS OVERLAY                      */}
      {/* ============================================ */}
      {showTeamPicker && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            role="presentation"
            onClick={() => setShowTeamPicker(false)}
          />

          {/* Modal */}
          <div className="relative z-10 w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col bg-[#111318] rounded-2xl border border-white/10 shadow-2xl shadow-black/50 overflow-hidden team-picker-enter">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <h2 className="text-xl font-bold text-white">
                  Pick Your Teams
                </h2>
                <p className="text-sm text-white/40 mt-0.5">
                  Follow teams to personalize your experience
                </p>
              </div>
              <button
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                onClick={() => setShowTeamPicker(false)}
              >
                <i className="fas fa-times text-white/60 text-sm" />
              </button>
            </div>

            {/* Search Input */}
            <div className="px-6 py-4 border-b border-white/[0.06]">
              <div className="relative">
                <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
                <input
                  className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/[0.06] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-blue-500/50 focus:bg-white/[0.08] transition-all"
                  placeholder="Search teams, leagues, or sports..."
                  ref={teamSearchInputRef}
                  type="text"
                  value={teamSearchQuery}
                  onChange={(e) => setTeamSearchQuery(e.target.value)}
                />
                {teamSearchQuery && (
                  <button
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    onClick={() => setTeamSearchQuery("")}
                  >
                    <i className="fas fa-times-circle text-sm" />
                  </button>
                )}
              </div>
            </div>

            {/* Scrollable Content */}
            <div
              className="flex-1 overflow-y-auto px-6 py-4"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(255,255,255,0.1) transparent",
              }}
            >
              {/* Currently Following Section */}
              {followedTeams.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-bold text-white/40 uppercase tracking-wider mb-3">
                    Following ({followedTeams.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {followedTeams.map((teamName) => (
                      <FollowButton
                        className="group"
                        entity={teamName}
                        key={teamName}
                        size="sm"
                        type="team"
                        variant="pill"
                        onFollowChange={() =>
                          setFollowedTeams([...getFavoriteTeams()])
                        }
                      />
                    ))}
                  </div>

                  {/* Superfan selector */}
                  <div className="mt-3 pt-3 border-t border-white/[0.06]">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="fas fa-star text-amber-400/60 text-[10px]" />
                      <span className="text-[10px] font-bold text-white/30 uppercase tracking-wider">
                        Superfan Mode
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {followedTeams.map((teamName) => (
                        <button
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all duration-200 ${
                            superfanTeam === teamName
                              ? "bg-amber-500/20 border border-amber-500/40 text-amber-300"
                              : "bg-white/[0.04] border border-white/[0.08] text-white/30 hover:bg-amber-500/10 hover:border-amber-500/20 hover:text-amber-300/60"
                          }`}
                          key={`sf-${teamName}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSuperfan(teamName);
                          }}
                        >
                          <i
                            className={`fas fa-star text-[8px] ${superfanTeam === teamName ? "text-amber-400" : "text-white/20"}`}
                          />
                          {teamName}
                        </button>
                      ))}
                    </div>
                    {superfanTeam && (
                      <p className="text-[10px] text-amber-400/40 mt-1.5">
                        Full-screen takeover when {superfanTeam} is live
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Top 3 Games For You - hide when user is typing a search */}
              {followedTeams.length > 0 && teamSearchQuery.length < 2 && (
                <div className="mb-6">
                  <h3 className="text-xs font-bold text-white/40 uppercase tracking-wider mb-3">
                    Top Games For You
                  </h3>
                  <div className="space-y-2">
                    {getTopGamesForUser().map((game, index) => (
                      <div
                        className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.07] hover:border-white/15 transition-all cursor-pointer"
                        key={game.id || index}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setShowTeamPicker(false);
                          navigateToEvent(game);
                        }}
                        onKeyDown={activateOnEnterOrSpace}
                      >
                        <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px] font-bold text-white/50 flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {game.awayTeamLogo && (
                            <img
                              alt=""
                              className="w-5 h-5 object-contain"
                              src={game.awayTeamLogo}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          )}
                          <span className="text-sm text-white/70 truncate">
                            {game.awayTeam}
                          </span>
                          <span className="text-xs text-white/30 font-bold mx-1">
                            {game.awayTeamScore ?? 0} -{" "}
                            {game.homeTeamScore ?? 0}
                          </span>
                          {game.homeTeamLogo && (
                            <img
                              alt=""
                              className="w-5 h-5 object-contain"
                              src={game.homeTeamLogo}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          )}
                          <span className="text-sm text-white/70 truncate">
                            {game.homeTeam}
                          </span>
                        </div>
                        {game.rating === "LIVE" && (
                          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/30 flex-shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 live-pulse" />
                            <span className="text-[9px] font-bold text-red-400 uppercase">
                              Live
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                    {getTopGamesForUser().length === 0 && (
                      <p className="text-sm text-white/30 text-center py-3">
                        No matching games right now
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Suggested Teams - same leagues as followed, hide during search */}
              {followedTeams.length > 0 &&
                teamSearchQuery.length < 2 &&
                suggestedTeams.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xs font-bold text-white/40 uppercase tracking-wider mb-3">
                      Suggested Teams
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {suggestedTeams.map((team) => (
                        <button
                          className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.07] hover:border-white/15 transition-all duration-200 text-left group"
                          key={team.name}
                          onClick={() => handleToggleTeam(team)}
                        >
                          {team.logo ? (
                            <img
                              alt=""
                              className="w-7 h-7 object-contain flex-shrink-0"
                              src={team.logo}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                              <i className="fas fa-shield-alt text-xs text-white/30" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-white/80 truncate">
                              {team.name}
                            </div>
                            <div className="text-[10px] text-white/25 truncate">
                              {team.league}
                            </div>
                          </div>
                          <div className="w-6 h-6 rounded-full bg-blue-500/15 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="fas fa-plus text-[9px] text-blue-400" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              {/* Search Results */}
              {teamSearchQuery.length >= 2 && (
                <div className="mb-4">
                  <h3 className="text-xs font-bold text-white/40 uppercase tracking-wider mb-3">
                    {teamSearchResults.length > 0
                      ? `Results (${teamSearchResults.length})`
                      : "No results found"}
                  </h3>
                  <div className="space-y-1">
                    {teamSearchResults.map((team) => {
                      const isFollowed = followedTeams.includes(team.name);

                      return (
                        <button
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-left ${
                            isFollowed
                              ? "bg-blue-500/10 border border-blue-500/20"
                              : "bg-transparent hover:bg-white/[0.04] border border-transparent"
                          }`}
                          key={team.name}
                          onClick={() => handleToggleTeam(team)}
                        >
                          {team.logo && (
                            <img
                              alt=""
                              className="w-8 h-8 object-contain flex-shrink-0"
                              src={team.logo}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white truncate">
                              {team.name}
                            </div>
                            <div className="text-[11px] text-white/30">
                              {team.league} &middot; {team.sport}
                            </div>
                          </div>
                          <div
                            className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                              isFollowed
                                ? "bg-blue-500 text-white"
                                : "bg-white/10 text-white/40 hover:bg-white/20"
                            }`}
                          >
                            <i
                              className={`fas ${isFollowed ? "fa-check" : "fa-plus"} text-xs`}
                            />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Empty state when no search */}
              {teamSearchQuery.length < 2 && followedTeams.length === 0 && (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/10 border border-blue-500/20 mb-4">
                    <i className="fas fa-search text-2xl text-blue-400/60" />
                  </div>
                  <p className="text-white/40 text-sm">
                    Search for teams to follow
                  </p>
                  <p className="text-white/25 text-xs mt-1">
                    Try &ldquo;Lakers&rdquo;, &ldquo;NFL&rdquo;, or
                    &ldquo;Arsenal&rdquo;
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            {followedTeams.length > 0 && (
              <div className="px-6 py-4 border-t border-white/10 bg-white/[0.02]">
                <button
                  className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-all duration-200 shadow-lg shadow-blue-500/20"
                  onClick={() => setShowTeamPicker(false)}
                >
                  Done &middot; Following {followedTeams.length} Team
                  {followedTeams.length !== 1 ? "s" : ""}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes livePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.85); }
        }
        .live-pulse {
          animation: livePulse 1.5s ease-in-out infinite;
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .hero-fade-in {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        .hero-fade-in-delay-1 {
          animation: fadeInUp 0.6s ease-out 0.1s forwards;
          opacity: 0;
        }
        .hero-fade-in-delay-2 {
          animation: fadeInUp 0.6s ease-out 0.2s forwards;
          opacity: 0;
        }
        .hero-fade-in-delay-3 {
          animation: fadeInUp 0.6s ease-out 0.3s forwards;
          opacity: 0;
        }
        .hero-fade-in-delay-4 {
          animation: fadeInUp 0.6s ease-out 0.4s forwards;
          opacity: 0;
        }

        @keyframes spotlightSlideOut {
          from { opacity: 1; transform: translateX(0); }
          to { opacity: 0; transform: translateX(-60px); }
        }
        @keyframes spotlightSlideIn {
          from { opacity: 0; transform: translateX(60px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .spotlight-slide {
          will-change: transform, opacity;
        }
        .spotlight-slide-out {
          animation: spotlightSlideOut 0.4s ease-in forwards;
        }
        .spotlight-slide-in {
          animation: spotlightSlideIn 0.5s ease-out forwards;
        }

        @keyframes followHeartBounce {
          0% { transform: scale(1); }
          30% { transform: scale(1.25); }
          60% { transform: scale(0.95); }
          100% { transform: scale(1); }
        }
        .follow-heart:active {
          animation: followHeartBounce 200ms cubic-bezier(0.68,-0.55,0.265,1.55);
        }

        @keyframes teamPickerEnter {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .team-picker-enter {
          animation: teamPickerEnter 0.2s ease-out forwards;
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        /* Superfan mode animations */
        @keyframes superfanPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.2); }
        }
        .superfan-pulse {
          animation: superfanPulse 2s ease-in-out infinite;
        }

        @keyframes superfanCalloutEnter {
          from { transform: translateX(-50%) translateY(20px); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }
        .superfan-callout-enter {
          animation: superfanCalloutEnter 400ms cubic-bezier(0.16,1,0.3,1) forwards;
        }

        /* Heartbeat pulse animation */
        @keyframes heartbeatPulse {
          0% { transform: scale(1); }
          15% { transform: scale(1.02); }
          30% { transform: scale(1); }
          45% { transform: scale(1.015); }
          60% { transform: scale(1); }
        }
        .heartbeat-pulse {
          animation: heartbeatPulse 1.2s ease-out;
        }

        /* Scrollbar for superfan feed */
        .scrollbar-thin::-webkit-scrollbar {
          width: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
        }

        @media (prefers-reduced-motion: reduce) {
          .live-pulse {
            animation: none;
          }
          .hero-fade-in,
          .hero-fade-in-delay-1,
          .hero-fade-in-delay-2,
          .hero-fade-in-delay-3,
          .hero-fade-in-delay-4 {
            animation: none;
            opacity: 1;
          }
          .spotlight-slide-out,
          .spotlight-slide-in {
            animation: none;
            opacity: 1;
            transform: none;
          }
          .superfan-pulse,
          .superfan-callout-enter,
          .heartbeat-pulse {
            animation: none;
          }
        }
      `}</style>
    </>
  );
};

export default WebPlayerSportsTest;
